import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import Modal from "react-modal";
import { FileText, Save, X, XCircle, Copy, Calendar } from "lucide-react";
import DatePicker, { CalendarContainer } from "react-datepicker";
import { createPortal } from "react-dom";
import "react-datepicker/dist/react-datepicker.css";
import ProductMaster from "./master/ProductMaster";
import { useSalesFormStore, useRestoredSalesForm, isInitialRestoredPickup } from "../services/useSalesFormStore";
import { useDraftForm } from "../context/drafts/DraftForm";
import { insertDraftTransaction } from "../apis/drafts";
import {
  insertTransactionByCode,
  editTransactionByCode,
  fetchTransactionByCode,
  fetchTransactionByCodeAndId,
  fetchTransactionPopupData,
  fetchInventoryAvailability,
  approveTransaction,
  insertJobCard,
  updateJobCardProcessStatus,
  editJobCard,
  createJobcard,
  updateWbomTranIdInWO,
  reviseQuotation,
  fetchTransactionNo,
  fetchBomByProduct,
} from "../apis/transaction";
import { fetchRouteCard } from "../apis/processCard";
import { capitalizeFirstLetterOfEachWord } from "../utils/Formatter";
import { uploadFile } from "../utils/uploadFile";
import {
  adoptAttachments,
  deleteAttachment,
  fetchAttachments,
  filterAttachmentsForRowValue,
} from "../apis/attachments";
import { useErrorHandler } from "../services/ErrorHandler";
import { displayToast } from "../utils/Toast";
import { useOutsideClick } from "../utils/useOutsideClick";
import ConfirmationModal from "./ConfirmationModal";
import RouteCardPopup from "./RouteCardPopup";
import { useMode } from "../context/ModeContext";
import "../styles/components/TransactionTable.css";

const DateCalendar = ({ children, onClear, className, ...props }) => {
  const [dropdownRow, setDropdownRow] = useState(null);
  const calendarRef = useCallback((node) => {
    setDropdownRow(
      node?.querySelector(".react-datepicker__header__dropdown") ?? null,
    );
  }, []);

  return (
    <div ref={calendarRef}>
      <CalendarContainer
        {...props}
        className={`${className || ""} clearable-datepicker-calendar`}
      >
        {children}
      </CalendarContainer>
      {dropdownRow &&
        onClear &&
        createPortal(
          <button
            type="button"
            className="react-datepicker__month-read-view calendar-clear-button"
            onClick={onClear}
          >
            Clear
          </button>,
          dropdownRow,
        )}
    </div>
  );
};

const AnalysisDateInput = React.forwardRef(
  ({ value, onClick, placeholder }, ref) => (
    <div
      className="analysis-date-input-box"
      onClick={onClick}
      ref={ref}
      tabIndex={0}
    >
      <span
        className={value ? "analysis-date-value" : "analysis-date-placeholder"}
      >
        {value || placeholder}
      </span>
      <Calendar size={16} className="analysis-date-icon" />
    </div>
  ),
);

const CALENDAR_MODIFIERS = [
  {
    name: "shiftY",
    fn({ y, placement }) {
      if (String(placement).startsWith("top")) {
        return { y: Math.max(10, y) };
      }
      return {};
    },
  },
];

const TransactionTable = ({
  rowData = [],
  transactionCode = "",
  isViewMode = false,
  isEditMode = false,
  setIsEditMode,
  transactionData,
  headFields,
  pickupMandatory,
  modeHighlight,
  type = "",
  isCopyMode = false,
  onRefresh = () => {},
  shouldNavigateAfterSubmit = true,
  workOrderStatus = "not_started",
  locationState = {},
  isAutoTransactionNo = false,
}) => {
  const navigate = useNavigate();
  const { tab, category } = useParams();
  const draftForm = useDraftForm();
  const restoredSnapshot = useRestoredSalesForm();
  const draftSubmission = draftForm && !isEditMode && !isCopyMode && type !== "JobCard" && !["WBOM", "BOM", "JC"].includes(transactionCode) ? draftForm : null;
  const submitLock = React.useRef(false);
  const formData = useSalesFormStore((state) => state.formData);
  const setPrimaryDetails = useSalesFormStore(
    (state) => state.setPrimaryDetails,
  );
  const setCustomerDetails = useSalesFormStore(
    (state) => state.setCustomerDetails,
  );
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [fileUploadLoading, setFileUploadLoading] = useState({});
  const { handleErrorResponse } = useErrorHandler();
  const { setMode } = useMode();
  const {
    selectedPickUpTransactionItems,
    setSelectedPickUpTransactionItems,
    pickUpData,
    selectedPickUpTransaction,
    selectedTransaction,
  } = useSalesFormStore();
  const setTransactionItems = useSalesFormStore(
    (state) => state.setTransactionItems,
  );
  const [popupItems, setPopupItems] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentField, setCurrentField] = useState(null);
  const [currentRowIndex, setCurrentRowIndex] = useState(null);
  const clearForm = useSalesFormStore((state) => state.clearForm);
  const clearTransactionState = useSalesFormStore(
    (state) => state.clearTransactionState,
  );
  const {
    otherDetailsData,
    stagedAttachments,
    addStagedAttachments,
    removeStagedAttachments,
    clearOtherDetails,
    clearStagedAttachments,
  } = useSalesFormStore();
  const fileInputRefs = React.useRef({});
  const [items, setItems] = useState([]);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showApproveConfirmation, setShowApproveConfirmation] = useState(false);
  const [actionType, setActionType] = useState("");
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);
  const [showJobCardRemoveConfirm, setShowJobCardRemoveConfirm] =
    useState(false);
  const [jobCardRowIndexToRemove, setJobCardRowIndexToRemove] = useState(null);

  const [showRejectReason, setShowRejectReason] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [popupHoveredProductId, setPopupHoveredProductId] = useState(null);
  const [popupProductAvailability, setPopupProductAvailability] = useState({});
  const [popupProductAvailabilityLoading, setPopupProductAvailabilityLoading] =
    useState({});
  const [isMaterialPopupOpen, setIsMaterialPopupOpen] = useState(false);
  const [materialPopupRowIndex, setMaterialPopupRowIndex] = useState(null);
  const [isOptionsPopupOpen, setIsOptionsPopupOpen] = useState(false);
  const [optionsPopupRowIndex, setOptionsPopupRowIndex] = useState(null);
  const [optionsPopupField, setOptionsPopupField] = useState(null);
  const [optionsPopupItems, setOptionsPopupItems] = useState([]);
  const isStockAllocationTransaction = transactionCode === "SA";
  const jobCardStatus = transactionData?.headDetails?.status;

  const normalizeCardToken = (value = "") =>
    String(value).trim().toUpperCase().replace(/\s+/g, "_");

  const isCreateJobCardField = (field = {}) => {
    const tokens = [
      normalizeCardToken(field.field_type),
      normalizeCardToken(field.data_type),
      normalizeCardToken(field.field_name),
    ];

    return tokens.some(
      (token) => token === "CREATE_JOBCARD" || token === "CREATE_JOB_CARD",
    );
  };

  const isCreateJobCardType = (value = "") => {
    const token = normalizeCardToken(value);
    return token === "CREATE_JOBCARD" || token === "CREATE_JOB_CARD";
  };

  const workOrderTypeLabel = useMemo(() => {
    const currentType = String(formData?.primaryDetails?.type || "")
      .trim()
      .toLowerCase();
    if (
      currentType === "job work" ||
      currentType === "jobwork" ||
      currentType === "job card" ||
      currentType === "jobcard"
    ) {
      return "Job Card";
    }
    return "Route Card";
  }, [formData?.primaryDetails?.type]);

  const isWorkOrderCompleted = [
    "finished",
    "finish",
    "completed",
    "complete",
    "done",
    "closed",
    "2",
  ].includes(
    String(workOrderStatus || "")
      .trim()
      .toLowerCase(),
  );

  const isRouteCardGreenForRow = (row) =>
    isRouteCardCreatedForRow(row) || isWorkOrderCompleted;

  const renderWorkOrderCardButton = (row, rowIndex) => (
    <button
      onClick={() => handleRouteCardClick(row, rowIndex)}
      className="route-card-btn"
      style={{
        cursor: "pointer",
        color: isRouteCardGreenForRow(row) ? "#4caf50" : "inherit",
      }}
    >
      {workOrderTypeLabel}
    </button>
  );

  const navigateToTransactionList = () => {
    if (!tab || !category) {
      navigate(-1);
      return;
    }
    sessionStorage.setItem(`clearCache_${tab}_${category}`, "true");
    navigate(`/${tab}/${category}`, {
      state: { transactionCode, forceRefresh: true },
      replace: true,
    });
  };

  const navigateToWorkOrderDetail = (wbomTranNo, wbomTranId) => {
    const woTranId =
      formData.primaryDetails?.wo_tran_id ||
      locationState?.transactionId ||
      locationState?.primaryDetails?.tran_id;
    if (!tab || !category) {
      navigateToTransactionList();
      return;
    }

    if (!woTranId) {
      const headDetails = {
        ...(locationState?.primaryDetails || {}),
        ...(wbomTranNo ? { bom_number: wbomTranNo } : {}),
        ...(wbomTranId ? { bom: String(wbomTranId) } : {}),
      };
      const itemDetails = locationState?.transactionItems || [];
      const customerDetails =
        locationState?.customerDetails || locationState?.company || {};

      setPrimaryDetails(headDetails);
      setTransactionItems(itemDetails);
      setCustomerDetails(customerDetails);

      sessionStorage.setItem(
        "returnFromBomDraftWorkOrder",
        JSON.stringify({ headDetails, itemDetails, customerDetails }),
      );
      sessionStorage.setItem(
        "pendingWorkOrderBomDraft",
        JSON.stringify({
          workOrderNo:
            headDetails.tran_no ||
            headDetails.workorder_no ||
            headDetails.wo_no,
          bom_number: headDetails.bom_number,
          bom: headDetails.bom,
        }),
      );
      navigate(-1);
      return;
    }

    navigate(`/${tab}/${category}/transactions`, {
      state: {
        ...locationState,
        transactionId: Number(woTranId),
        transactionCode: "WO",
        forceRefresh: true,
      },
      replace: true,
    });
  };

  const navigateToParentTransactionDetail = () => {
    const parentTranId = locationState?.returnTransactionId;
    const parentTranCode = locationState?.returnTransactionCode;
    const returnLocationState = locationState?.returnLocationState || {};

    if (!tab || !category || !parentTranId || !parentTranCode) {
      return;
    }

    navigate(`/${tab}/${category}/transactions`, {
      state: {
        ...returnLocationState,
        transactionId: Number(parentTranId),
        transactionCode: parentTranCode,
        forceRefresh: true,
      },
      replace: true,
    });
  };

  const normalizeMaterialName = (name = "") =>
    name.trim().toUpperCase().replace(/\s+/g, "");

  const MATERIAL_FIELD_MAP = useMemo(
    () => ({
      CU: {
        densityField: "cu",
        priceField: "cu_price",
        weldingPriceField: "welding_cu_price",
      },
      AL: {
        densityField: "al_density",
        priceField: "al_price",
        weldingPriceField: "welding_al_price",
      },
      GI: {
        densityField: "gi",
        priceField: "gi_price",
        weldingPriceField: "welding_gi_price",
      },
      CR: {
        densityField: "cr_hr",
        priceField: "cr_hr_price",
        weldingPriceField: "welding_cr_hr_price",
      },
      HR: {
        densityField: "hr_density",
        priceField: "hr_price_per_kg",
        weldingPriceField: "welding_cr_hr_price",
      },
      "CR/HR": {
        densityField: "cr_hr",
        priceField: "cr_hr_price",
        weldingPriceField: "welding_cr_hr_price",
      },
      SS: {
        densityField: "ss_density",
        priceField: "ss_price",
        weldingPriceField: "welding_ss_price",
      },
      BRASS: {
        densityField: "brass_density",
        priceField: "brass_price",
        weldingPriceField: "welding_brass_price",
      },
    }),
    [],
  );

  const EC_MATERIAL_OPTIONS = useMemo(() => {
    const materialField = rowData.find((f) => f.field_name === "material");
    if (!materialField || !materialField.options) return [];
    return materialField.options
      .split(",")
      .map((name) => name.trim())
      .filter((name) => name)
      .map((name) => {
        const materialKey = normalizeMaterialName(name);
        return {
          name,
          materialKey,
          densityField: MATERIAL_FIELD_MAP[materialKey]?.densityField || "",
          priceField: MATERIAL_FIELD_MAP[materialKey]?.priceField || "",
          weldingPriceField:
            MATERIAL_FIELD_MAP[materialKey]?.weldingPriceField || "",
        };
      });
  }, [rowData, MATERIAL_FIELD_MAP]);

  const getMaterialOption = useCallback(
    (materialName = "") => {
      const normalizedMaterialName = normalizeMaterialName(materialName);
      return (
        EC_MATERIAL_OPTIONS.find(
          (m) =>
            m.materialKey === normalizedMaterialName || m.name === materialName,
        ) || null
      );
    },
    [EC_MATERIAL_OPTIONS],
  );

  useEffect(() => {
    if (transactionCode !== "EC") return;
    console.log(
      "[DEBUG] EC useEffect FIRED, primaryDetails keys:",
      Object.keys(formData.primaryDetails || {}).length,
    );
    console.log(
      "[DEBUG] profit_percentage:",
      formData.primaryDetails?.profit_percentage,
      "packing_percentage:",
      formData.primaryDetails?.packing_percentage,
      "handling_perncentage:",
      formData.primaryDetails?.handling_perncentage,
    );
    console.log(
      "[DEBUG] All primaryDetails keys:",
      Object.keys(formData.primaryDetails || {}),
    );

    setRows((prevRows) => {
      console.log(
        "[DEBUG] EC useEffect prevRows count:",
        prevRows.length,
        "first row keys:",
        Object.keys(prevRows[0] || {}).length,
      );
      let changed = false;
      const updatedRows = prevRows.map((row) => {
        const length = parseFloat(row.length_mm) || 0;
        const width = parseFloat(row.width_mm) || 0;
        const thick = parseFloat(row.thick_mm) || 0;

        const newPowderAreaSqft =
          length && width ? (((length * width) / 92903) * 2).toFixed(2) : "";
        const pcPricePerSqft =
          parseFloat(formData.primaryDetails?.pc_price_per_sqft) || 0;
        const newPowderCoatingCost =
          newPowderAreaSqft && pcPricePerSqft
            ? (parseFloat(newPowderAreaSqft) * pcPricePerSqft).toFixed(2)
            : "";

        const materialOption = row.material
          ? getMaterialOption(row.material)
          : null;
        const density = materialOption
          ? parseFloat(
              formData.primaryDetails?.[materialOption.densityField],
            ) || 0
          : 0;
        let newWeight = "";
        if (length && width && thick && density) {
          newWeight = ((length * width * thick * density) / 1000000).toFixed(2);
        }

        const existingWeight = row.item_weight_kg;
        if (
          existingWeight !== undefined &&
          existingWeight !== "" &&
          existingWeight !== null
        ) {
          newWeight = existingWeight;
        }
        const qtyNo = parseFloat(row.qty_no) || 0;
        const newWithQuantityWt =
          newWeight && qtyNo ? (parseFloat(newWeight) * qtyNo).toFixed(2) : "";
        const scrapPct =
          parseFloat(formData.primaryDetails?.scrap_percentage) || 0;
        let newScrapWeight = "";
        if (newWeight && scrapPct) {
          newScrapWeight = ((parseFloat(newWeight) * scrapPct) / 100).toFixed(
            4,
          );
        }
        const newFinalWeight =
          newWeight || newScrapWeight
            ? (
                parseFloat(newWeight || 0) + parseFloat(newScrapWeight || 0)
              ).toFixed(2)
            : "";
        const matPrice =
          parseFloat(formData.primaryDetails?.[materialOption?.priceField]) ||
          0;
        const newRawMtlCost =
          newFinalWeight && matPrice
            ? (parseFloat(newFinalWeight) * matPrice).toFixed(2)
            : "";
        const newPerimeter =
          length && width ? ((2 * (length + width)) / 1000).toFixed(2) : "";
        const newAreaInSqftKg =
          length && width
            ? ((length / 1000) * (width / 1000) * 10.76 * 2).toFixed(2)
            : "";
        const pricePerSqftKg = parseFloat(row.price_per_sqft_kg) || 0;
        const newTotalCoatingCost =
          newAreaInSqftKg && pricePerSqftKg
            ? (parseFloat(newAreaInSqftKg) * pricePerSqftKg).toFixed(2)
            : "";
        const newPunchingLaserPerimeter = newPerimeter;
        const ratePerMeterHit = parseFloat(row.rate_per_meter_hit) || 0;
        const newTotalLaserPunchingCost =
          newPunchingLaserPerimeter && ratePerMeterHit && thick
            ? (
                parseFloat(newPunchingLaserPerimeter) *
                ratePerMeterHit *
                thick
              ).toFixed(2)
            : "";
        const laserMtrPrice =
          parseFloat(formData.primaryDetails?.laser_mtr_price) || 0;
        const newLaserCost =
          newPerimeter && laserMtrPrice && thick
            ? (parseFloat(newPerimeter) * laserMtrPrice * thick).toFixed(2)
            : "";
        const newStrokeNo =
          length && width ? ((2 * (length + width)) / 40).toFixed(2) : "";
        let newPunchingCost = "";
        if (newStrokeNo && row.stroke_type) {
          if (row.stroke_type === "PUNCHING") {
            const punchPrice =
              parseFloat(formData.primaryDetails?.punching_stroke_price) || 0;
            if (punchPrice)
              newPunchingCost = (parseFloat(newStrokeNo) * punchPrice).toFixed(
                4,
              );
          } else if (row.stroke_type === "NIBBLING") {
            const nibblePrice =
              parseFloat(formData.primaryDetails?.nibbling_stroke_price) || 0;
            if (nibblePrice)
              newPunchingCost = (parseFloat(newStrokeNo) * nibblePrice).toFixed(
                4,
              );
          }
        }
        let newBendingCost = "";
        const bendMappings = [
          { item: "no_of_bend_0_2_mtr_no", head: "0_2_mtr_stroke" },
          { item: "no_of_bend_2_3_mtr_no", head: "2_3_mtr_stroke" },
          { item: "no_of_bend_3_6_mtr_no", head: "3_6_mtr_stroke" },
        ];
        for (const bm of bendMappings) {
          const bendVal = parseFloat(row[bm.item]) || 0;
          if (bendVal) {
            const headPrice =
              parseFloat(formData.primaryDetails?.[bm.head]) || 0;
            if (headPrice) newBendingCost = (bendVal * headPrice).toFixed(2);
            break;
          }
        }
        const noOfBends = parseFloat(row.no_of_bends) || 0;
        const perBendCost = parseFloat(row.per_bend_cost) || 0;
        const newTotalBendingCost =
          noOfBends && perBendCost ? (noOfBends * perBendCost).toFixed(2) : "";
        let newWeldingCost = "";
        const weldingLengthMm = parseFloat(row.welding_length_mm) || 0;
        const weldingLen = weldingLengthMm
          ? (weldingLengthMm / 25.4).toFixed(2)
          : "";
        if (weldingLen && materialOption?.weldingPriceField) {
          const weldPrice =
            parseFloat(
              formData.primaryDetails?.[materialOption.weldingPriceField],
            ) || 0;
          if (weldPrice)
            newWeldingCost = (parseFloat(weldingLen) * weldPrice).toFixed(2);
        }
        const weldingCostPerInch = parseFloat(row.welding_cost_per_inch) || 0;
        const weldingSettingTimeCost =
          parseFloat(row.welding_setting_time_cost) || 0;
        const newTotalWeldingCost =
          weldingCostPerInch || parseFloat(weldingLen) || weldingSettingTimeCost
            ? (
                weldingCostPerInch * (parseFloat(weldingLen) || 0) +
                weldingSettingTimeCost
              ).toFixed(2)
            : "";
        const fastnersCostPerNo = parseFloat(row.fastners_cost_per_no) || 0;
        const quantity = parseFloat(row.quantity) || 0;
        const pressingCost = parseFloat(row.pressing_cost) || 0;
        const fastnersNumerator =
          fastnersCostPerNo * quantity + quantity * pressingCost;
        const newTotalFastnersCost = fastnersNumerator
          ? fastnersNumerator.toFixed(2)
          : "";
        const rawMtlCostValue = parseFloat(newRawMtlCost) || 0;
        const totalLaserPunchingCostValue =
          parseFloat(newTotalLaserPunchingCost) || 0;
        const totalBendingCostValue = parseFloat(newTotalBendingCost) || 0;
        const totalWeldingCostValue = parseFloat(newTotalWeldingCost) || 0;
        const totalFastnersCostValue = parseFloat(newTotalFastnersCost) || 0;
        const totalCoatingCostValue = parseFloat(newTotalCoatingCost) || 0;
        const totalProcessSum =
          rawMtlCostValue +
          totalLaserPunchingCostValue +
          totalBendingCostValue +
          totalWeldingCostValue +
          totalFastnersCostValue +
          totalCoatingCostValue;
        const newTotalProcessCost = totalProcessSum
          ? totalProcessSum.toFixed(2)
          : "";
        const profitPercentage =
          parseFloat(formData.primaryDetails?.profit_percentage) || 0;
        const newProfictCost =
          newTotalProcessCost && profitPercentage
            ? (
                parseFloat(newTotalProcessCost) *
                (profitPercentage / 100)
              ).toFixed(2)
            : "";
        const packingPercentage =
          parseFloat(formData.primaryDetails?.packing_percentage) || 0;
        const newPackingCost =
          newTotalProcessCost && packingPercentage
            ? (
                parseFloat(newTotalProcessCost) *
                (packingPercentage / 100)
              ).toFixed(2)
            : "";
        const handlingPercentage =
          parseFloat(formData.primaryDetails?.handling_perncentage) || 0;
        console.log(
          "[DEBUG] handlingPercentage:",
          handlingPercentage,
          "raw value:",
          formData.primaryDetails?.handling_perncentage,
          "newTotalProcessCost:",
          newTotalProcessCost,
        );
        const newOverheadAndHandlingCost =
          newTotalProcessCost && handlingPercentage
            ? (
                parseFloat(newTotalProcessCost) *
                (handlingPercentage / 100)
              ).toFixed(2)
            : "";
        console.log(
          "[DEBUG] newOverheadAndHandlingCost:",
          newOverheadAndHandlingCost,
        );
        const newPartCostPerNo = (
          (parseFloat(newTotalProcessCost) || 0) +
          (parseFloat(newProfictCost) || 0) +
          (parseFloat(newPackingCost) || 0) +
          (parseFloat(newOverheadAndHandlingCost) || 0)
        ).toFixed(2);
        const newTotalPartCost =
          newPartCostPerNo && qtyNo
            ? (parseFloat(newPartCostPerNo) * qtyNo).toFixed(2)
            : "";
        if (
          row.item_weight_kg !== newWeight ||
          row.with_quantity_wt_in_kg !== newWithQuantityWt ||
          row.scrap_weight_kg !== newScrapWeight ||
          row.final_weight_kg !== newFinalWeight ||
          row.raw_mtl_cost !== newRawMtlCost ||
          row.perimeter_devl_size_mm !== newPerimeter ||
          row.area_in_sqft_kg !== newAreaInSqftKg ||
          row.total_coating_cost !== newTotalCoatingCost ||
          row.punching_laser_perimeter !== newPunchingLaserPerimeter ||
          row.total_laser_punching_cost !== newTotalLaserPunchingCost ||
          row.laser_cost !== newLaserCost ||
          row.stroke_no !== newStrokeNo ||
          row.punching_cost !== newPunchingCost ||
          row.bending_cost !== newBendingCost ||
          row.welding_length_mm2 !== weldingLen ||
          row.total_bending_cost !== newTotalBendingCost ||
          row.welding_cost !== newWeldingCost ||
          row.total_welding_cost !== newTotalWeldingCost ||
          row.total_fastners_cost !== newTotalFastnersCost ||
          row.total_process_cost !== newTotalProcessCost ||
          row.profict_cost !== newProfictCost ||
          row.packing_cost !== newPackingCost ||
          row.overhead_and_handling_cost !== newOverheadAndHandlingCost ||
          row.part_cost_per_no !== newPartCostPerNo ||
          row.total_part_cost !== newTotalPartCost ||
          row.powder_area_sqft !== newPowderAreaSqft ||
          row.powder_coating_cost !== newPowderCoatingCost
        ) {
          changed = true;
          return {
            ...row,
            item_weight_kg: newWeight,
            with_quantity_wt_in_kg: newWithQuantityWt,
            scrap_weight_kg: newScrapWeight,
            final_weight_kg: newFinalWeight,
            raw_mtl_cost: newRawMtlCost,
            perimeter_devl_size_mm: newPerimeter,
            area_in_sqft_kg: newAreaInSqftKg,
            total_coating_cost: newTotalCoatingCost,
            punching_laser_perimeter: newPunchingLaserPerimeter,
            total_laser_punching_cost: newTotalLaserPunchingCost,
            laser_cost: newLaserCost,
            stroke_no: newStrokeNo,
            punching_cost: newPunchingCost,
            bending_cost: newBendingCost,
            welding_length_mm2: weldingLen,
            total_bending_cost: newTotalBendingCost,
            welding_cost: newWeldingCost,
            total_welding_cost: newTotalWeldingCost,
            total_fastners_cost: newTotalFastnersCost,
            total_process_cost: newTotalProcessCost,
            profict_cost: newProfictCost,
            packing_cost: newPackingCost,
            overhead_and_handling_cost: newOverheadAndHandlingCost,
            part_cost_per_no: newPartCostPerNo,
            total_part_cost: newTotalPartCost,
            powder_area_sqft: newPowderAreaSqft,
            powder_coating_cost: newPowderCoatingCost,
          };
        }
        return row;
      });
      if (changed) {
        console.log(
          "[DEBUG] EC useEffect CHANGED rows, returning updatedRows count:",
          updatedRows.length,
        );
        return updatedRows;
      }
      console.log("[DEBUG] EC useEffect NO CHANGE, returning prevRows");
      return prevRows;
    });
  }, [transactionCode, getMaterialOption, formData.primaryDetails]);

  const allTableFields = useMemo(() => {
    return [...rowData].sort((a, b) => {
      if (a.field_name === "product_name") return -1;
      if (b.field_name === "product_name") return 1;
      return a.order_no - b.order_no;
    });
  }, [rowData]);

  const visibleTableFields = useMemo(() => {
    return allTableFields.filter((field) => !field.is_internal_field);
  }, [allTableFields]);

  const EC_HEADING_GROUP_BY_FIELD = useMemo(
    () => ({
      set_part_no: "COSTING PATTERN FOR SHEET METAL COMPONENTS",
      part_name: "COSTING PATTERN FOR SHEET METAL COMPONENTS",
      qty_no: "COSTING PATTERN FOR SHEET METAL COMPONENTS",
      material: "COSTING PATTERN FOR SHEET METAL COMPONENTS",
      length_mm: "MATERIAL PRICE",
      width_mm: "MATERIAL PRICE",
      thick_mm: "MATERIAL PRICE",
      item_weight_kg: "MATERIAL PRICE",
      scrap_weight_kg: "MATERIAL PRICE",
      final_weight_kg: "MATERIAL PRICE",
      raw_mtl_cost: "MATERIAL PRICE",
      punching_laser_perimeter: "PIERCING COST",
      rate_per_meter_hit: "PIERCING COST",
      total_laser_punching_cost: "PIERCING COST",
      bending_cost: "BENDING COST",
      bending_radius: "BENDING COST",
      no_of_bends: "BENDING COST",
      per_bend_cost: "BENDING COST",
      welding_length_mm: "WELDING COST",
      welding_length_mm2: "WELDING COST",
      welding_cost_per_inch: "WELDING COST",
      welding_setting_time_cost: "WELDING COST",
      total_welding_cost: "WELDING COST",
      grind_sandring: "FINISHING",
      part_name_size: "FASTNERS COST",
      make: "FASTNERS COST",
      quantity: "FASTNERS COST",
      fastners_cost_per_no: "FASTNERS COST",
      pressing_cost: "FASTNERS COST",
      total_fastners_cost: "FASTNERS COST",
      surface_finish: "COATING",
      area_in_sqft_kg: "COATING",
      price_per_sqft_kg: "COATING",
      total_coating_cost: "COATING",
    }),
    [],
  );

  const EC_YELLOW_HEADING_FIELDS = useMemo(
    () =>
      new Set([
        "item_weight_kg",
        "raw_mtl_cost",
        "total_laser_punching_cost",
        "total_bending_cost",
        "total_welding_cost",
        "total_fastners_cost",
        "total_coating_cost",
      ]),
    [],
  );

  const EC_CALCULATED_FIELDS = useMemo(
    () =>
      new Set([
        "with_quantity_wt_in_kg",
        "scrap_weight_kg",
        "final_weight_kg",
        "raw_mtl_cost",
        "powder_area_sqft",
        "powder_coating_cost",
        "perimeter_devl_size_mm",
        "area_in_sqft_kg",
        "total_coating_cost",
        "punching_laser_perimeter",
        "total_laser_punching_cost",
        "laser_cost",
        "stroke_no",
        "punching_cost",
        "total_bending_cost",
        "welding_length_mm2",
        "welding_cost",
        "total_welding_cost",
        "total_fastners_cost",
        "total_process_cost",
        "profict_cost",
        "packing_cost",
        "overhead_and_handling_cost",
        "part_cost_per_no",
        "total_part_cost",
      ]),
    [],
  );

  const handlefetchTransactionPopupData = async (dbQuery) => {
    try {
      const response = await fetchTransactionPopupData(dbQuery);
      if (response.message) {
        displayToast(response.message, "error");
      } else {
        setPopupItems(response.items);
        setIsPopupOpen(true);
      }
    } catch (error) {
      displayToast("Failed to fetch transaction data", "error");
    }
  };

  const getPopupProductId = useCallback((item = {}) => {
    return item.inventory_product_id || item.product_id || item.id || null;
  }, []);

  const handleCloseProductPopup = useCallback(() => {
    setPopupHoveredProductId(null);
    setIsPopupOpen(false);
  }, []);

  const fetchProductAvailabilitySnapshot = useCallback(async (productId) => {
    const response = await fetchInventoryAvailability(productId);
    const stockData = Array.isArray(response)
      ? response[0]
      : Array.isArray(response?.items)
        ? response.items[0]
        : response?.data || response;

    return {
      currentQty: stockData?.current_qty ?? "-",
      availableQty: stockData?.available_qty ?? "-",
      allocatedQty: stockData?.allocated_qty ?? "-",
      error: stockData?.error?.description || "",
    };
  }, []);

  const handlePopupItemMouseEnter = useCallback(
    async (item) => {
      if (!isStockAllocationTransaction) return;

      const productId = getPopupProductId(item);
      if (!productId) return;

      const productKey = String(productId);
      setPopupHoveredProductId(productKey);

      if (
        popupProductAvailability[productKey] ||
        popupProductAvailabilityLoading[productKey]
      ) {
        return;
      }

      setPopupProductAvailabilityLoading((prev) => ({
        ...prev,
        [productKey]: true,
      }));

      try {
        const stockSnapshot = await fetchProductAvailabilitySnapshot(productId);

        setPopupProductAvailability((prev) => ({
          ...prev,
          [productKey]: stockSnapshot,
        }));
      } catch (error) {
        setPopupProductAvailability((prev) => ({
          ...prev,
          [productKey]: {
            currentQty: "-",
            availableQty: "-",
            allocatedQty: "-",
            error: "Failed to fetch inventory",
          },
        }));
      } finally {
        setPopupProductAvailabilityLoading((prev) => ({
          ...prev,
          [productKey]: false,
        }));
      }
    },
    [
      fetchProductAvailabilitySnapshot,
      getPopupProductId,
      isStockAllocationTransaction,
      popupProductAvailability,
      popupProductAvailabilityLoading,
    ],
  );

  const handlePopupItemMouseLeave = useCallback(() => {
    setPopupHoveredProductId(null);
  }, []);

  const popUpModalRef = useOutsideClick(() => {
    handleCloseProductPopup();
  });

  const searchProductInputRef = useRef(null);

  useEffect(() => {
    if (isPopupOpen && type !== "JobCard") {
      searchProductInputRef.current?.focus();
    }
  }, [isPopupOpen, type]);

  const handleFieldClick = (field, index) => {
    if (
      field.field_name === "material" &&
      EC_MATERIAL_OPTIONS.length > 0 &&
      !isViewMode
    ) {
      setMaterialPopupRowIndex(index);
      setIsMaterialPopupOpen(true);
      return;
    }
    if (field.options && field.options.trim() !== "" && !isViewMode) {
      const items = field.options
        .split(",")
        .map((o) => o.trim())
        .filter(Boolean);
      setOptionsPopupItems(items);
      setOptionsPopupField(field);
      setOptionsPopupRowIndex(index);
      setIsOptionsPopupOpen(true);
      return;
    }
    if (!field.is_internal_field && field.is_query) {
      setCurrentField(field);
      setCurrentRowIndex(index);
      handlefetchTransactionPopupData(field.db_query);
    }
  };

  const materialPopupRef = useOutsideClick(() => {
    setIsMaterialPopupOpen(false);
  });

  const optionsPopupRef = useOutsideClick(() => {
    setIsOptionsPopupOpen(false);
  });

  const handleOptionsSelect = (value) => {
    const updatedRows = [...rows];
    const currentRow = { ...updatedRows[optionsPopupRowIndex] };
    currentRow[optionsPopupField.field_name] = value;
    if (
      optionsPopupField.field_name === "stroke_type" &&
      currentRow.stroke_no
    ) {
      if (value === "PUNCHING") {
        const punchPrice =
          parseFloat(formData.primaryDetails?.punching_stroke_price) || 0;
        currentRow.punching_cost = punchPrice
          ? (parseFloat(currentRow.stroke_no) * punchPrice).toFixed(2)
          : "";
      } else if (value === "NIBBLING") {
        const nibblePrice =
          parseFloat(formData.primaryDetails?.nibbling_stroke_price) || 0;
        currentRow.punching_cost = nibblePrice
          ? (parseFloat(currentRow.stroke_no) * nibblePrice).toFixed(2)
          : "";
      } else {
        currentRow.punching_cost = "";
      }
    }
    updatedRows[optionsPopupRowIndex] = currentRow;
    setRows(updatedRows);
    setTransactionItems(updatedRows);
    setIsOptionsPopupOpen(false);
  };

  const handleMaterialSelect = (material) => {
    const updatedRows = [...rows];
    const currentRow = { ...updatedRows[materialPopupRowIndex] };
    currentRow.material = material.name;
    const density =
      parseFloat(formData.primaryDetails?.[material.densityField]) || 0;
    const length = parseFloat(currentRow.length_mm) || 0;
    const width = parseFloat(currentRow.width_mm) || 0;
    const thick = parseFloat(currentRow.thick_mm) || 0;
    if (length && width && thick && density) {
      currentRow.item_weight_kg = (
        (length * width * thick * density) /
        1000000
      ).toFixed(2);
    } else {
      currentRow.item_weight_kg = "";
    }
    const qtyNo = parseFloat(currentRow.qty_no) || 0;
    currentRow.with_quantity_wt_in_kg =
      currentRow.item_weight_kg && qtyNo
        ? (parseFloat(currentRow.item_weight_kg) * qtyNo).toFixed(2)
        : "";
    const scrapPct = parseFloat(formData.primaryDetails?.scrap_percentage) || 0;
    if (currentRow.item_weight_kg && scrapPct) {
      currentRow.scrap_weight_kg = (
        (parseFloat(currentRow.item_weight_kg) * scrapPct) /
        100
      ).toFixed(2);
    } else {
      currentRow.scrap_weight_kg = "";
    }
    currentRow.final_weight_kg =
      currentRow.item_weight_kg || currentRow.scrap_weight_kg
        ? (
            parseFloat(currentRow.item_weight_kg || 0) +
            parseFloat(currentRow.scrap_weight_kg || 0)
          ).toFixed(2)
        : "";
    const matPrice =
      parseFloat(formData.primaryDetails?.[material.priceField]) || 0;
    currentRow.raw_mtl_cost =
      currentRow.final_weight_kg && matPrice
        ? (parseFloat(currentRow.final_weight_kg) * matPrice).toFixed(2)
        : "";
    const mLength = parseFloat(currentRow.length_mm) || 0;
    const mWidth = parseFloat(currentRow.width_mm) || 0;
    currentRow.powder_area_sqft =
      mLength && mWidth ? (((mLength * mWidth) / 92903) * 2).toFixed(2) : "";
    const pcPrice = parseFloat(formData.primaryDetails?.pc_price_per_sqft) || 0;
    currentRow.powder_coating_cost =
      currentRow.powder_area_sqft && pcPrice
        ? (parseFloat(currentRow.powder_area_sqft) * pcPrice).toFixed(2)
        : "";
    currentRow.perimeter_devl_size_mm =
      mLength && mWidth ? ((2 * (mLength + mWidth)) / 1000).toFixed(2) : "";
    currentRow.area_in_sqft_kg =
      mLength && mWidth
        ? ((mLength / 1000) * (mWidth / 1000) * 10.76 * 2).toFixed(2)
        : "";
    const pricePerSqftKg = parseFloat(currentRow.price_per_sqft_kg) || 0;
    currentRow.total_coating_cost =
      currentRow.area_in_sqft_kg && pricePerSqftKg
        ? (parseFloat(currentRow.area_in_sqft_kg) * pricePerSqftKg).toFixed(2)
        : "";
    currentRow.punching_laser_perimeter = currentRow.perimeter_devl_size_mm;
    const ratePerMeterHit = parseFloat(currentRow.rate_per_meter_hit) || 0;
    const mThick = parseFloat(currentRow.thick_mm) || 0;
    currentRow.total_laser_punching_cost =
      currentRow.punching_laser_perimeter && ratePerMeterHit && mThick
        ? (
            parseFloat(currentRow.punching_laser_perimeter) *
            ratePerMeterHit *
            mThick
          ).toFixed(2)
        : "";
    const laserMtrPrice =
      parseFloat(formData.primaryDetails?.laser_mtr_price) || 0;
    currentRow.laser_cost =
      currentRow.perimeter_devl_size_mm && laserMtrPrice && mThick
        ? (
            parseFloat(currentRow.perimeter_devl_size_mm) *
            laserMtrPrice *
            mThick
          ).toFixed(2)
        : "";
    currentRow.stroke_no =
      mLength && mWidth ? ((2 * (mLength + mWidth)) / 40).toFixed(2) : "";
    if (currentRow.stroke_no && currentRow.stroke_type) {
      if (currentRow.stroke_type === "PUNCHING") {
        const punchPrice =
          parseFloat(formData.primaryDetails?.punching_stroke_price) || 0;
        currentRow.punching_cost = punchPrice
          ? (parseFloat(currentRow.stroke_no) * punchPrice).toFixed(2)
          : "";
      } else if (currentRow.stroke_type === "NIBBLING") {
        const nibblePrice =
          parseFloat(formData.primaryDetails?.nibbling_stroke_price) || 0;
        currentRow.punching_cost = nibblePrice
          ? (parseFloat(currentRow.stroke_no) * nibblePrice).toFixed(2)
          : "";
      } else {
        currentRow.punching_cost = "";
      }
    } else {
      currentRow.punching_cost = "";
    }
    const noOfBends = parseFloat(currentRow.no_of_bends) || 0;
    const perBendCost = parseFloat(currentRow.per_bend_cost) || 0;
    currentRow.total_bending_cost =
      noOfBends && perBendCost ? (noOfBends * perBendCost).toFixed(2) : "";
    const weldingLengthMm = parseFloat(currentRow.welding_length_mm) || 0;
    currentRow.welding_length_mm2 = weldingLengthMm
      ? (weldingLengthMm / 25.4).toFixed(2)
      : "";
    const weldingLen = parseFloat(currentRow.welding_length_mm2) || 0;
    if (weldingLen && material.weldingPriceField) {
      const weldPrice =
        parseFloat(formData.primaryDetails?.[material.weldingPriceField]) || 0;
      currentRow.welding_cost = weldPrice
        ? (weldingLen * weldPrice).toFixed(2)
        : "";
    } else {
      currentRow.welding_cost = "";
    }
    const weldingCostPerInch =
      parseFloat(currentRow.welding_cost_per_inch) || 0;
    const weldingSettingTimeCost =
      parseFloat(currentRow.welding_setting_time_cost) || 0;
    currentRow.total_welding_cost =
      weldingCostPerInch || weldingLen || weldingSettingTimeCost
        ? (weldingCostPerInch * weldingLen + weldingSettingTimeCost).toFixed(2)
        : "";
    const fastnersCostPerNo = parseFloat(currentRow.fastners_cost_per_no) || 0;
    const quantity = parseFloat(currentRow.quantity) || 0;
    const pressingCost = parseFloat(currentRow.pressing_cost) || 0;
    const fastnersNumerator =
      fastnersCostPerNo * quantity + quantity * pressingCost;
    currentRow.total_fastners_cost = fastnersNumerator
      ? fastnersNumerator.toFixed(2)
      : "";
    const rawMtlCostValue = parseFloat(currentRow.raw_mtl_cost) || 0;
    const totalLaserPunchingCostValue =
      parseFloat(currentRow.total_laser_punching_cost) || 0;
    const totalBendingCostValue =
      parseFloat(currentRow.total_bending_cost) || 0;
    const totalWeldingCostValue =
      parseFloat(currentRow.total_welding_cost) || 0;
    const totalFastnersCostValue =
      parseFloat(currentRow.total_fastners_cost) || 0;
    const totalCoatingCostValue =
      parseFloat(currentRow.total_coating_cost) || 0;
    const totalProcessSum =
      rawMtlCostValue +
      totalLaserPunchingCostValue +
      totalBendingCostValue +
      totalWeldingCostValue +
      totalFastnersCostValue +
      totalCoatingCostValue;
    currentRow.total_process_cost = totalProcessSum
      ? totalProcessSum.toFixed(2)
      : "";
    const profitPercentage =
      parseFloat(formData.primaryDetails?.profit_percentage) || 0;
    currentRow.profict_cost =
      currentRow.total_process_cost && profitPercentage
        ? (
            parseFloat(currentRow.total_process_cost) *
            (profitPercentage / 100)
          ).toFixed(2)
        : "";
    const packingPercentage =
      parseFloat(formData.primaryDetails?.packing_percentage) || 0;
    currentRow.packing_cost =
      currentRow.total_process_cost && packingPercentage
        ? (
            parseFloat(currentRow.total_process_cost) *
            (packingPercentage / 100)
          ).toFixed(2)
        : "";
    const handlingPercentage =
      parseFloat(formData.primaryDetails?.handling_perncentage) || 0;
    currentRow.overhead_and_handling_cost =
      currentRow.total_process_cost && handlingPercentage
        ? (
            parseFloat(currentRow.total_process_cost) *
            (handlingPercentage / 100)
          ).toFixed(2)
        : "";
    currentRow.part_cost_per_no = (
      (parseFloat(currentRow.total_process_cost) || 0) +
      (parseFloat(currentRow.profict_cost) || 0) +
      (parseFloat(currentRow.packing_cost) || 0) +
      (parseFloat(currentRow.overhead_and_handling_cost) || 0)
    ).toFixed(2);
    currentRow.total_part_cost =
      currentRow.part_cost_per_no && qtyNo
        ? (parseFloat(currentRow.part_cost_per_no) * qtyNo).toFixed(2)
        : "";
    updatedRows[materialPopupRowIndex] = currentRow;
    setRows(updatedRows);
    setTransactionItems(updatedRows);
    setIsMaterialPopupOpen(false);
  };

  const handlePopupItemClick = async (item) => {
    const updatedRows = [...rows];
    const currentRow = { ...updatedRows[currentRowIndex] };

    if (type === "JobCard") {
      currentRow[currentField.field_name] = item.name;
    } else {
      const displayName =
        item.Product_Name || item.product_name || item.inventory_product_id;
      const selectedProductId = getPopupProductId(item);
      const normalizedSelectedProductId = selectedProductId
        ? Number(selectedProductId)
        : null;
      const productKey = normalizedSelectedProductId
        ? String(normalizedSelectedProductId)
        : "";
      let stockSnapshot = productKey
        ? popupProductAvailability[productKey]
        : null;

      if (
        isStockAllocationTransaction &&
        normalizedSelectedProductId &&
        !stockSnapshot
      ) {
        try {
          stockSnapshot = await fetchProductAvailabilitySnapshot(
            normalizedSelectedProductId,
          );
          setPopupProductAvailability((prev) => ({
            ...prev,
            [productKey]: stockSnapshot,
          }));
        } catch (error) {
          stockSnapshot = null;
        }
      }

      const availableQty = Number(stockSnapshot?.availableQty ?? NaN);
      if (
        isStockAllocationTransaction &&
        Number.isFinite(availableQty) &&
        availableQty <= 0
      ) {
        displayToast(
          `${displayName} is not selectable because available quantity is 0.`,
          "error",
        );
        return;
      }

      const currentFieldName = String(currentField?.field_name || "");
      const linkedIdFieldName = currentFieldName.endsWith("_name")
        ? `${currentFieldName.slice(0, -5)}_id`
        : "";

      currentRow[currentField.field_name] = displayName;
      if (currentFieldName === "product_name") {
        currentRow.product_name = displayName;
        currentRow.product_id = normalizedSelectedProductId;
      }

      if (
        linkedIdFieldName &&
        (Object.prototype.hasOwnProperty.call(currentRow, linkedIdFieldName) ||
          allTableFields.some(
            (field) => field.field_name === linkedIdFieldName,
          ))
      ) {
        currentRow[linkedIdFieldName] = normalizedSelectedProductId;
      }

      if (
        currentFieldName === "product_name" &&
        (Object.prototype.hasOwnProperty.call(
          currentRow,
          "inventory_product_id",
        ) ||
          allTableFields.some(
            (field) => field.field_name === "inventory_product_id",
          ))
      ) {
        currentRow.inventory_product_id = normalizedSelectedProductId;
      }

      if (item.UOM) {
        currentRow.uom = item.UOM;
      } else if (item.uom) {
        currentRow.uom = item.uom;
      } else if (item.unit) {
        currentRow.unit = item.unit;
      } else if (item.unit_of_measurement) {
        currentRow.unit_of_measurement = item.unit_of_measurement;
      }

      if (
        ["CO", "SO"].includes(effectiveTransactionCode) &&
        currentFieldName === "product_name" &&
        normalizedSelectedProductId
      ) {
        const targetRowIndex = currentRowIndex;
        fetchBomByProduct(normalizedSelectedProductId)
          .then((bomResponse) => {
            if (!bomResponse?.head_data) return;
            const bomNo = bomResponse.head_data.tran_no || "";
            const bomFieldNames = ["bom_number", "bom_no"].filter((name) =>
              allTableFields.some((field) => field.field_name === name),
            );
            if (bomFieldNames.length > 0 && bomNo) {
              setRows((prevRows) => {
                const nextRows = [...prevRows];
                const targetRow = { ...nextRows[targetRowIndex] };
                bomFieldNames.forEach((name) => {
                  targetRow[name] = bomNo;
                });
                nextRows[targetRowIndex] = targetRow;
                setTransactionItems(nextRows);
                return nextRows;
              });
            }
            displayToast(
              `${displayName} already has a BOM${
                bomNo ? ` (${bomNo})` : ""
              } \u2014 it will be auto-loaded into the Work Order BOM`,
              "info",
            );
          })
          .catch(() => {});
      }
    }

    updatedRows[currentRowIndex] = currentRow;
    setRows(updatedRows);
    setTransactionItems(updatedRows);
    handleCloseProductPopup();
  };

  const taxFields = useMemo(() => {
    return allTableFields.filter((field) => field.is_tax);
  }, [allTableFields]);

  const createEmptyRow = useCallback(() => {
    const emptyRow = allTableFields.reduce((acc, field) => {
      acc[field.field_name] = "";
      return acc;
    }, {});

    emptyRow.product_id = null;
    emptyRow.inspection_report = "";

    return emptyRow;
  }, [allTableFields]);

  const [rows, setRows] = useState(() => restoredSnapshot?.formData.transactionItems || [createEmptyRow()]);
  const [itemAttachmentsByField, setItemAttachmentsByField] = useState({});

  const itemAttachmentParentIds = useMemo(
    () => [
      ...new Set(
        rows.map((row) => row.tran_id).filter((parentId) => parentId != null),
      ),
    ],
    [rows],
  );
  // stable key so row edits do not re-trigger the attachment fetch
  const itemAttachmentParentKey = itemAttachmentParentIds.join(",");

  useEffect(() => {
    if (
      (!isViewMode && !isEditMode) ||
      isCopyMode ||
      !transactionCode ||
      !itemAttachmentParentKey
    ) {
      setItemAttachmentsByField({});
      return;
    }

    const parentIds = itemAttachmentParentKey.split(",");

    const loadItemAttachments = async () => {
      try {
        const responses = await Promise.all(
          parentIds.map((parentId) =>
            fetchAttachments({
              tranCode: transactionCode,
              scope: "item",
              parentId,
            }),
          ),
        );
        const groupedAttachments = responses.reduce(
          (groups, response, responseIndex) => {
            const attachments = Array.isArray(response)
              ? response
              : response?.attachments ||
                response?.items ||
                response?.data ||
                [];
            attachments.forEach((attachment) => {
              const fieldName = attachment.field_name;
              if (fieldName) {
                const key = `${parentIds[responseIndex]}:${fieldName}`;
                groups[key] = [...(groups[key] || []), attachment];
              }
            });
            return groups;
          },
          {},
        );
        setItemAttachmentsByField(groupedAttachments);
      } catch (error) {
        console.error("Failed to load item attachments:", error);
        setItemAttachmentsByField({});
      }
    };

    loadItemAttachments();
  }, [
    isViewMode,
    isEditMode,
    isCopyMode,
    itemAttachmentParentKey,
    transactionCode,
  ]);

  const calculateQualityFields = useCallback(
    (row) => {
      const updatedRow = { ...row };

      if (
        transactionCode === "GRN" &&
        (Object.prototype.hasOwnProperty.call(updatedRow, "quantity") ||
          Object.prototype.hasOwnProperty.call(updatedRow, "received_qty"))
      ) {
        const orderQuantity = parseFloat(updatedRow.quantity) || 0;
        const receivedQuantity = parseFloat(updatedRow.received_qty) || 0;
        const hasAcceptedQuantity =
          updatedRow.accepted_qty !== "" &&
          updatedRow.accepted_qty !== null &&
          updatedRow.accepted_qty !== undefined;
        const acceptedQuantity = hasAcceptedQuantity
          ? parseFloat(updatedRow.accepted_qty) || 0
          : 0;

        if (Object.prototype.hasOwnProperty.call(updatedRow, "rejected_qty")) {
          updatedRow.rejected_qty = hasAcceptedQuantity
            ? (receivedQuantity - acceptedQuantity).toFixed(2)
            : "";
        }
        if (Object.prototype.hasOwnProperty.call(updatedRow, "po_balance")) {
          updatedRow.po_balance = (orderQuantity - receivedQuantity).toFixed(2);
        }
      }

      const getFieldValue = (variations, defaultValue = 0) => {
        for (const variation of variations) {
          if (
            updatedRow.hasOwnProperty(variation) &&
            updatedRow[variation] !== "" &&
            updatedRow[variation] != null
          ) {
            return parseFloat(updatedRow[variation]) || defaultValue;
          }
        }
        return defaultValue;
      };

      const qualityCalculationFields = [
        "reject_pcs",
        "reject",
        "rejects_pcs",
        "rejects",
        "cost_per_piece",
        "cost_per_pcs",
        "cost_per_piece_inr",
        "rework_cost",
        "rework_cost_inr",
        "planned_quantity",
        "planned_qty",
        "actual_quantity",
        "actual_qty",
        "planned_date",
        "planned_delivery_date",
        "actual_delivery_date",
      ];

      const hasQualityFields = Object.keys(updatedRow).some((key) =>
        qualityCalculationFields.some(
          (qf) =>
            key.toLowerCase().includes(qf.toLowerCase()) ||
            qf.toLowerCase().includes(key.toLowerCase()),
        ),
      );

      if (hasQualityFields) {
        const rejectPcs = getFieldValue([
          "reject_pcs",
          "reject",
          "rejects_pcs",
          "rejects",
        ]);
        const costPerPiece = getFieldValue([
          "cost_per_piece",
          "cost_per_pcs",
          "cost_per_piece_inr",
        ]);
        const copqDefectReject = rejectPcs * costPerPiece;

        const copqDefectFields = [
          "copq_defect_reject",
          "copq_defect",
          "copq_defe",
        ];
        for (const copqField of copqDefectFields) {
          if (updatedRow.hasOwnProperty(copqField)) {
            updatedRow[copqField] = copqDefectReject.toFixed(2);
            break;
          }
        }

        const reworkCost = getFieldValue(["rework_cost", "rework_cost_inr"]);
        const copqRework = reworkCost;

        if (updatedRow.hasOwnProperty("copq_rework")) {
          updatedRow.copq_rework = copqRework.toFixed(2);
        }

        const totalCopq = copqDefectReject + copqRework;

        if (updatedRow.hasOwnProperty("total_copq")) {
          updatedRow.total_copq = totalCopq.toFixed(2);
        }

        const plannedQuantity = getFieldValue([
          "planned_quantity",
          "planned_qty",
        ]);
        const actualQuantity = getFieldValue(["actual_quantity", "actual_qty"]);
        const balanceQuantity = plannedQuantity - actualQuantity;

        const balanceFields = ["balance_quantity", "balance_qty"];
        for (const balanceField of balanceFields) {
          if (updatedRow.hasOwnProperty(balanceField)) {
            updatedRow[balanceField] = balanceQuantity.toFixed(2);
            break;
          }
        }

        let deliveryPercentage = 0;
        if (plannedQuantity > 0) {
          deliveryPercentage = (actualQuantity / plannedQuantity) * 100;
        }

        const deliveryFields = ["delivered"];
        for (const deliveryField of deliveryFields) {
          if (updatedRow.hasOwnProperty(deliveryField)) {
            updatedRow[deliveryField] = deliveryPercentage.toFixed(2);
            break;
          }
        }
      }

      return updatedRow;
    },
    [transactionCode],
  );

  useEffect(() => {
    const shouldUpdateRows = formData.transactionItems?.length > 0;
    console.log(
      "[DEBUG] ROW LOADING useEffect fired, transactionItems count:",
      formData.transactionItems?.length,
      "allTableFields count:",
      allTableFields.length,
      "shouldUpdateRows:",
      shouldUpdateRows,
    );
    if (shouldUpdateRows) {
      const formattedRows = formData.transactionItems.map((item) => {
        if (item.selected) {
          return item;
        }

        const formattedRow = allTableFields.reduce(
          (acc, field) => {
            acc[field.field_name] = item[field.field_name] ?? "";
            return acc;
          },
          { ...item },
        );

        formattedRow.product_id = item.product_id || null;

        return formattedRow;
      });

      const rowsWithCalculations = formattedRows.map((row) => {
        const qualityRow = calculateQualityFields(row);

        if (!isViewMode) {
          const hasField = (name) =>
            allTableFields.some((field) => field.field_name === name);
          const rate = parseFloat(qualityRow.rate) || 0;
          const quantity = parseFloat(qualityRow.quantity) || 0;
          if (hasField("rate") && (rate || quantity)) {
            if (hasField("amount")) {
              qualityRow.amount = (rate * quantity).toFixed(2);
            }
            [
              { percentageField: "cgst_per", amountField: "cgst_amt" },
              { percentageField: "sgst_per", amountField: "sgst_amt" },
              { percentageField: "igst_per", amountField: "igst_amt" },
            ].forEach(({ percentageField, amountField }) => {
              if (!hasField(amountField)) return;
              const percentage = parseFloat(qualityRow[percentageField]);
              if (!Number.isNaN(percentage)) {
                qualityRow[amountField] = (
                  (rate * quantity * percentage) /
                  100
                ).toFixed(2);
              }
            });
            const grossValue = calculateGrossAmount(qualityRow);
            if (hasField("gross_amt")) {
              qualityRow.gross_amt = grossValue;
            }
            if (hasField("gross_amount")) {
              qualityRow.gross_amount = grossValue;
            }
          }
        }

        if (transactionCode === "EC" && qualityRow.total_process_cost) {
          const totalProcessCost =
            parseFloat(qualityRow.total_process_cost) || 0;
          const profitPct =
            parseFloat(formData.primaryDetails?.profit_percentage) || 0;
          const packingPct =
            parseFloat(formData.primaryDetails?.packing_percentage) || 0;
          const handlingPct =
            parseFloat(formData.primaryDetails?.handling_perncentage) || 0;

          if (profitPct) {
            qualityRow.profict_cost = (
              totalProcessCost *
              (profitPct / 100)
            ).toFixed(2);
          }
          if (packingPct) {
            qualityRow.packing_cost = (
              totalProcessCost *
              (packingPct / 100)
            ).toFixed(2);
          }
          if (handlingPct) {
            qualityRow.overhead_and_handling_cost = (
              totalProcessCost *
              (handlingPct / 100)
            ).toFixed(2);
          }

          const qtyNo = parseFloat(qualityRow.qty_no) || 0;
          qualityRow.part_cost_per_no = (
            totalProcessCost +
            (parseFloat(qualityRow.profict_cost) || 0) +
            (parseFloat(qualityRow.packing_cost) || 0) +
            (parseFloat(qualityRow.overhead_and_handling_cost) || 0)
          ).toFixed(2);
          if (qtyNo) {
            qualityRow.total_part_cost = (
              parseFloat(qualityRow.part_cost_per_no) * qtyNo
            ).toFixed(2);
          }
        }
        return qualityRow;
      });

      setRows(rowsWithCalculations);
    }
  }, [formData.transactionItems, allTableFields, calculateQualityFields]);

  const computeOtif = (row) => {
    const plannedDateRaw =
      row.planned_date ||
      row.planned_delivery_date ||
      formData?.primaryDetails?.planned_delivery_date ||
      "";
    const actualDeliveryDateRaw =
      row.actual_delivery_date ||
      formData?.primaryDetails?.actual_delivery_date ||
      "";

    const parseDateParts = (val) => {
      if (!val) return null;
      const normalized = String(val).trim().replace(/\//g, "-");
      const parts = normalized.split("-");
      let year, month, day;
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          [year, month, day] = parts.map((p) => parseInt(p, 10));
        } else {
          [day, month, year] = parts.map((p) => parseInt(p, 10));
        }
      }
      if (
        Number.isInteger(year) &&
        Number.isInteger(month) &&
        Number.isInteger(day)
      ) {
        return { year, month, day };
      }
      const d = new Date(normalized);
      if (isNaN(d.getTime())) return null;
      return {
        year: d.getUTCFullYear(),
        month: d.getUTCMonth() + 1,
        day: d.getUTCDate(),
      };
    };

    const plannedDateObj = parseDateParts(plannedDateRaw);
    const actualDateObj = parseDateParts(actualDeliveryDateRaw);

    if (plannedDateObj && actualDateObj) {
      const sameDate =
        plannedDateObj.year === actualDateObj.year &&
        plannedDateObj.month === actualDateObj.month &&
        plannedDateObj.day === actualDateObj.day;
      if (sameDate) {
        return "YES";
      }
    }

    return "NO";
  };

  const parseSerialNo = (serialNo) => {
    if (!serialNo) return null;
    const parsed = parseInt(String(serialNo).replace("SN-", ""), 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  // Saved items can have gaps in serial_no; index-based numbering would overwrite an existing item.
  const assignSerialNumbers = (sourceRows) => {
    let nextSerialNo = sourceRows.reduce(
      (max, row) => Math.max(max, parseSerialNo(row.serial_no) || 0),
      0,
    );
    const usedSerialNos = new Set();
    return sourceRows.map((row) => {
      let serialNo = parseSerialNo(row.serial_no);
      if (!serialNo || usedSerialNos.has(serialNo)) {
        nextSerialNo += 1;
        serialNo = nextSerialNo;
      }
      usedSerialNos.add(serialNo);
      return { row, serialNo };
    });
  };

  const addRow = () => {
    if (isViewMode) return;
    const newRow = {
      ...createEmptyRow(),
    };
    const updatedRows = [...rows, newRow];
    setRows(updatedRows);
    setTransactionItems(updatedRows);
  };

  const removeRow = (index) => {
    if (isViewMode) return;
    if (rows.length === 1) {
      displayToast("Cannot remove the last row", "warning");
      return;
    }

    const row = rows[index];
    const isSavedRow = !!row?.tran_id;

    if (type === "JobCard" && isSavedRow) {
      setJobCardRowIndexToRemove(index);
      setShowJobCardRemoveConfirm(true);
      return;
    }

    handleRowRemoval(index);
  };

  const handleRowRemoval = (index) => {
    setRows((prevRows) => {
      const removedRow = prevRows[index];
      const updatedRows = prevRows.filter((_, idx) => idx !== index);
      setTransactionItems(updatedRows);

      if (removedRow?.id) {
        const updatedData = selectedPickUpTransactionItems.filter(
          (item) => item.id !== removedRow.id,
        );
        setSelectedPickUpTransactionItems(updatedData);
      }

      return updatedRows;
    });
  };

  const calculateTotalQuantity = useCallback(() => {
    return rows.reduce((total, row) => {
      const quantity = parseFloat(row.quantity) || 0;
      return total + quantity;
    }, 0);
  }, [rows]);

  useEffect(() => {
    if (isInitialRestoredPickup(restoredSnapshot, { selectedPickUpTransactionItems, selectedPickUpTransaction, selectedTransaction })) return;
    if (selectedPickUpTransactionItems?.length > 0) {
      const pickUpConfigs = Array.isArray(pickUpData)
        ? pickUpData
        : pickUpData?.head_fields || pickUpData?.item_fields
          ? [pickUpData]
          : Object.values(pickUpData || {});
      const selectedPickUpConfig =
        pickUpConfigs.find(
          (config) => config?.tran_code === selectedTransaction?.tran_code,
        ) || pickUpConfigs[0];
      const itemFields = selectedPickUpConfig?.item_fields || [];
      const headFields = selectedPickUpConfig?.head_fields || [];

      const filteredTransactions = selectedPickUpTransactionItems.map(
        (item) => {
          const filteredItem = {};

          itemFields.forEach(
            ({ field_name, copy_to_field_name, copy_to_table_type }) => {
              if (copy_to_table_type === "item") {
                filteredItem[copy_to_field_name] = item[field_name] ?? "";

                const rate = parseFloat(filteredItem.rate) || 0;
                const quantity = parseFloat(filteredItem.quantity) || 0;

                if (
                  (copy_to_field_name === "rate" ||
                    copy_to_field_name === "quantity") &&
                  rate &&
                  quantity
                ) {
                  filteredItem.amount = (
                    parseFloat(filteredItem.rate) *
                    parseFloat(filteredItem.quantity)
                  ).toFixed(2);
                }

                const gstFields = [
                  { percentageField: "cgst_per", amountField: "cgst_amt" },
                  { percentageField: "sgst_per", amountField: "sgst_amt" },
                  { percentageField: "igst_per", amountField: "igst_amt" },
                ];

                gstFields.forEach(({ percentageField, amountField }) => {
                  if (copy_to_field_name === percentageField) {
                    const percentage =
                      parseFloat(filteredItem[percentageField]) ?? 0;
                    filteredItem[amountField] = (
                      (rate * quantity * percentage) /
                      100
                    ).toFixed(2);
                  }
                });

                filteredItem.gross_amt = calculateGrossAmount(filteredItem);
              }
            },
          );

          headFields.forEach(
            ({ field_name, copy_to_field_name, copy_to_table_type }) => {
              if (
                copy_to_table_type === "item" &&
                item.hasOwnProperty(copy_to_field_name)
              ) {
                filteredItem[copy_to_field_name] =
                  selectedPickUpTransaction[selectedTransaction.tran_code]?.[
                    field_name
                  ] ?? "";
              }
            },
          );

          filteredItem.product_id = item.product_id;
          filteredItem.id = item.id;
          filteredItem.selected = item.selected;

          return filteredItem;
        },
      );

      const updatedRows = filteredTransactions.map((row) => ({
        ...row,
        amount:
          row.rate && row.quantity
            ? (parseFloat(row.rate) * parseFloat(row.quantity)).toFixed(2)
            : "0.00",
      }));
      setRows(updatedRows);
      setTransactionItems(updatedRows);
    }
  }, [
    selectedPickUpTransactionItems,
    selectedPickUpTransaction,
    selectedTransaction,
    pickUpData,
    restoredSnapshot,
  ]);

  const resetTransactionTable = useCallback(() => {
    if (!isViewMode && !selectedPickUpTransactionItems) {
      const emptyRow = createEmptyRow();
      setRows([emptyRow]);
      setTransactionItems([emptyRow]);
    }
  }, [
    isViewMode,
    createEmptyRow,
    selectedPickUpTransactionItems,
    setTransactionItems,
  ]);

  useEffect(() => {
    return () => {
      if (!isViewMode && !selectedPickUpTransactionItems) {
        resetTransactionTable();
      }
    };
  }, [isViewMode, resetTransactionTable, selectedPickUpTransactionItems]);

  useEffect(() => {
    if (isEditMode) {
      setMode("edit");
    } else if (isViewMode) {
      setMode("view");
    } else {
      setMode("add");
    }
  }, [isEditMode, isViewMode, setMode]);

  const calculateGrossAmount = useCallback(
    (row) => {
      const baseAmount =
        (parseFloat(row.rate) || 0) * (parseFloat(row.quantity) || 0);

      let grossAmount = baseAmount;

      taxFields.forEach((taxField) => {
        if (taxField.field_name.includes("amt") && taxField.is_tax) {
          const taxAmount = parseFloat(row[taxField.field_name]) || 0;
          if (taxField.tax_type === "+") {
            grossAmount += taxAmount;
          } else if (taxField.tax_type === "-") {
            grossAmount -= taxAmount;
          }
        }
      });

      return grossAmount.toFixed(2);
    },
    [taxFields],
  );

  const calculateTotals = useCallback(() => {
    return rows.reduce(
      (totals, row) => {
        const rate = parseFloat(row.rate) || 0;
        const quantity = parseFloat(row.quantity) || 0;
        const lineTotal = rate * quantity;
        const grossAmount = parseFloat(calculateGrossAmount(row)) || 0;

        return {
          subtotal: totals.subtotal + lineTotal,
          grossTotal: totals.grossTotal + grossAmount,
        };
      },
      { subtotal: 0, grossTotal: 0 },
    );
  }, [rows, calculateGrossAmount]);

  const handleInputChange = (index, field, value) => {
    if (isViewMode) return;

    const updatedRows = [...rows];
    const currentRow = { ...updatedRows[index] };

    const normalizedField = String(field || "")
      .trim()
      .toLowerCase();
    const isAllocationQtyField =
      normalizedField === "allocated_qty" ||
      normalizedField === "allocated_quantity";
    const productAvailability = currentRow.product_id
      ? popupProductAvailability[String(currentRow.product_id)]
      : null;
    const availableQty = Number(productAvailability?.availableQty ?? NaN);

    if (
      isStockAllocationTransaction &&
      isAllocationQtyField &&
      Number(value) > 0 &&
      Number.isFinite(availableQty) &&
      availableQty <= 0
    ) {
      displayToast(
        "Available quantity is 0. Allocation is not allowed.",
        "error",
      );
      currentRow[field] = "";
      updatedRows[index] = currentRow;
      setRows(updatedRows);
      setTransactionItems(updatedRows);
      return;
    }

    currentRow[field] = value;

    const bendFields = [
      { item: "no_of_bend_0_2_mtr_no", head: "0_2_mtr_stroke" },
      { item: "no_of_bend_2_3_mtr_no", head: "2_3_mtr_stroke" },
      { item: "no_of_bend_3_6_mtr_no", head: "3_6_mtr_stroke" },
    ];
    const activeBend = bendFields.find((b) => b.item === field);
    if (activeBend && value) {
      bendFields.forEach((b) => {
        if (b.item !== field) currentRow[b.item] = "";
      });
      const headPrice =
        parseFloat(formData.primaryDetails?.[activeBend.head]) || 0;
      currentRow.bending_cost = headPrice
        ? (parseFloat(value) * headPrice).toFixed(2)
        : "";
    } else if (activeBend && !value) {
      currentRow.bending_cost = "";
    }

    if (field === "welding_length_mm" || field === "welding_length") {
      currentRow.welding_length_mm2 = value
        ? (parseFloat(value) * 25.4).toFixed(2)
        : "";
      const materialOpt = EC_MATERIAL_OPTIONS.find(
        (m) => m.materialKey === normalizeMaterialName(currentRow.material),
      );
      if (currentRow.welding_length_mm2 && materialOpt?.weldingPriceField) {
        const weldPrice =
          parseFloat(
            formData.primaryDetails?.[materialOpt.weldingPriceField],
          ) || 0;
        currentRow.welding_cost = weldPrice
          ? (parseFloat(currentRow.welding_length_mm2) * weldPrice).toFixed(2)
          : "";
      } else {
        currentRow.welding_cost = "";
      }
      const weldingLen = parseFloat(currentRow.welding_length_mm2) || 0;
      const weldingCostPerInch =
        parseFloat(currentRow.welding_cost_per_inch) || 0;
      const weldingSettingTimeCost =
        parseFloat(currentRow.welding_setting_time_cost) || 0;
      currentRow.total_welding_cost =
        weldingCostPerInch || weldingLen || weldingSettingTimeCost
          ? (weldingCostPerInch * weldingLen + weldingSettingTimeCost).toFixed(
              2,
            )
          : "";
    }

    if (field === "rate" || field === "quantity") {
      const rate = parseFloat(currentRow.rate) || 0;
      const quantity = parseFloat(currentRow.quantity) || 0;
      currentRow.amount = (rate * quantity).toFixed(2);
    }

    if (
      transactionCode === "GRN" &&
      ["quantity", "received_qty", "accepted_qty"].includes(field)
    ) {
      const orderQuantity = parseFloat(currentRow.quantity) || 0;
      const receivedQuantity = parseFloat(currentRow.received_qty) || 0;
      const hasAcceptedQuantity =
        currentRow.accepted_qty !== "" &&
        currentRow.accepted_qty !== null &&
        currentRow.accepted_qty !== undefined;
      const acceptedQuantity = hasAcceptedQuantity
        ? parseFloat(currentRow.accepted_qty) || 0
        : 0;
      currentRow.rejected_qty = hasAcceptedQuantity
        ? (receivedQuantity - acceptedQuantity).toFixed(2)
        : "";
      currentRow.po_balance = (orderQuantity - receivedQuantity).toFixed(2);
    }

    if (
      ["cgst_per", "sgst_per", "igst_per", "rate", "quantity"].includes(field)
    ) {
      const rate = parseFloat(currentRow.rate) || 0;
      const quantity = parseFloat(currentRow.quantity) || 0;

      const gstFields = [
        { percentageField: "cgst_per", amountField: "cgst_amt" },
        { percentageField: "sgst_per", amountField: "sgst_amt" },
        { percentageField: "igst_per", amountField: "igst_amt" },
      ];

      gstFields.forEach(({ percentageField, amountField }) => {
        const percentage = parseFloat(currentRow[percentageField]) || 0;
        currentRow[amountField] = (
          (rate * quantity * percentage) /
          100
        ).toFixed(2);
      });
    }

    const qualityCalculationFields = [
      "reject_pcs",
      "reject",
      "rejects_pcs",
      "rejects",
      "cost_per_piece",
      "cost_per_pcs",
      "cost_per_piece_inr",
      "rework_cost",
      "rework_cost_inr",
      "planned_quantity",
      "planned_qty",
      "actual_quantity",
      "actual_qty",
      "planned_date",
      "planned_delivery_date",
      "actual_delivery_date",
    ];

    const hasQualityFields = Object.keys(currentRow).some((key) =>
      qualityCalculationFields.some(
        (qf) =>
          key.toLowerCase().includes(qf.toLowerCase()) ||
          qf.toLowerCase().includes(key.toLowerCase()),
      ),
    );

    if (hasQualityFields || qualityCalculationFields.includes(field)) {
      const rowWithCalculations = calculateQualityFields(currentRow);
      Object.assign(currentRow, rowWithCalculations);
    }

    if (
      transactionCode === "EC" &&
      [
        "length_mm",
        "width_mm",
        "thick_mm",
        "material",
        "stroke_type",
        "qty_no",
        "item_weight_kg",
        "rate_per_meter_hit",
        "no_of_bends",
        "per_bend_cost",
        "welding_length_mm",
        "welding_cost_per_inch",
        "welding_setting_time_cost",
        "fastners_cost_per_no",
        "quantity",
        "pressing_cost",
        "price_per_sqft_kg",
        "area_in_sqft_kg",
        "total_process_cost",
        "profict_cost",
        "packing_cost",
        "overhead_and_handling_cost",
        "part_cost_per_no",
      ].includes(field)
    ) {
      const materialOption = EC_MATERIAL_OPTIONS.find(
        (m) => m.materialKey === normalizeMaterialName(currentRow.material),
      );
      const density = materialOption
        ? parseFloat(formData.primaryDetails?.[materialOption.densityField]) ||
          0
        : 0;
      const length = parseFloat(currentRow.length_mm) || 0;
      const width = parseFloat(currentRow.width_mm) || 0;
      const thick = parseFloat(currentRow.thick_mm) || 0;
      if (field !== "item_weight_kg") {
        if (length && width && thick && density) {
          currentRow.item_weight_kg = (
            (length * width * thick * density) /
            1000000
          ).toFixed(2);
        } else {
          currentRow.item_weight_kg = "";
        }
      }
      const qtyNo = parseFloat(currentRow.qty_no) || 0;
      currentRow.with_quantity_wt_in_kg =
        currentRow.item_weight_kg && qtyNo
          ? (parseFloat(currentRow.item_weight_kg) * qtyNo).toFixed(2)
          : "";
      const scrapPct =
        parseFloat(formData.primaryDetails?.scrap_percentage) || 0;
      if (currentRow.item_weight_kg && scrapPct) {
        currentRow.scrap_weight_kg = (
          (parseFloat(currentRow.item_weight_kg) * scrapPct) /
          100
        ).toFixed(2);
      } else {
        currentRow.scrap_weight_kg = "";
      }
      currentRow.final_weight_kg =
        currentRow.item_weight_kg || currentRow.scrap_weight_kg
          ? (
              parseFloat(currentRow.item_weight_kg || 0) +
              parseFloat(currentRow.scrap_weight_kg || 0)
            ).toFixed(2)
          : "";
      const materialOpt = EC_MATERIAL_OPTIONS.find(
        (m) => m.materialKey === normalizeMaterialName(currentRow.material),
      );
      const matPrice = materialOpt
        ? parseFloat(formData.primaryDetails?.[materialOpt.priceField]) || 0
        : 0;
      currentRow.raw_mtl_cost =
        currentRow.final_weight_kg && matPrice
          ? (parseFloat(currentRow.final_weight_kg) * matPrice).toFixed(2)
          : "";
      currentRow.powder_area_sqft =
        length && width ? (((length * width) / 92903) * 2).toFixed(2) : "";
      const pcPriceSqft =
        parseFloat(formData.primaryDetails?.pc_price_per_sqft) || 0;
      currentRow.powder_coating_cost =
        currentRow.powder_area_sqft && pcPriceSqft
          ? (parseFloat(currentRow.powder_area_sqft) * pcPriceSqft).toFixed(2)
          : "";
      currentRow.perimeter_devl_size_mm =
        length && width ? ((2 * (length + width)) / 1000).toFixed(2) : "";
      currentRow.area_in_sqft_kg =
        length && width
          ? ((length / 1000) * (width / 1000) * 10.76 * 2).toFixed(2)
          : "";
      const pricePerSqftKg = parseFloat(currentRow.price_per_sqft_kg) || 0;
      currentRow.total_coating_cost =
        currentRow.area_in_sqft_kg && pricePerSqftKg
          ? (parseFloat(currentRow.area_in_sqft_kg) * pricePerSqftKg).toFixed(2)
          : "";
      currentRow.punching_laser_perimeter = currentRow.perimeter_devl_size_mm;
      const ratePerMeterHit = parseFloat(currentRow.rate_per_meter_hit) || 0;
      currentRow.total_laser_punching_cost =
        currentRow.punching_laser_perimeter && ratePerMeterHit && thick
          ? (
              parseFloat(currentRow.punching_laser_perimeter) *
              ratePerMeterHit *
              thick
            ).toFixed(2)
          : "";
      const laserMtrPrice =
        parseFloat(formData.primaryDetails?.laser_mtr_price) || 0;
      currentRow.laser_cost =
        currentRow.perimeter_devl_size_mm && laserMtrPrice && thick
          ? (
              parseFloat(currentRow.perimeter_devl_size_mm) *
              laserMtrPrice *
              thick
            ).toFixed(2)
          : "";
      currentRow.stroke_no =
        length && width ? ((2 * (length + width)) / 40).toFixed(2) : "";
      if (currentRow.stroke_no && currentRow.stroke_type) {
        if (currentRow.stroke_type === "PUNCHING") {
          const punchPrice =
            parseFloat(formData.primaryDetails?.punching_stroke_price) || 0;
          currentRow.punching_cost = punchPrice
            ? (parseFloat(currentRow.stroke_no) * punchPrice).toFixed(2)
            : "";
        } else if (currentRow.stroke_type === "NIBBLING") {
          const nibblePrice =
            parseFloat(formData.primaryDetails?.nibbling_stroke_price) || 0;
          currentRow.punching_cost = nibblePrice
            ? (parseFloat(currentRow.stroke_no) * nibblePrice).toFixed(2)
            : "";
        } else {
          currentRow.punching_cost = "";
        }
      } else {
        currentRow.punching_cost = "";
      }
      const noOfBends = parseFloat(currentRow.no_of_bends) || 0;
      const perBendCost = parseFloat(currentRow.per_bend_cost) || 0;
      currentRow.total_bending_cost =
        noOfBends && perBendCost ? (noOfBends * perBendCost).toFixed(2) : "";
      const weldingLengthMm = parseFloat(currentRow.welding_length_mm) || 0;
      currentRow.welding_length_mm2 = weldingLengthMm
        ? (weldingLengthMm / 25.4).toFixed(2)
        : "";
      const ecWeldingLen = parseFloat(currentRow.welding_length_mm2) || 0;
      if (ecWeldingLen && materialOption?.weldingPriceField) {
        const weldPrice =
          parseFloat(
            formData.primaryDetails?.[materialOption.weldingPriceField],
          ) || 0;
        currentRow.welding_cost = weldPrice
          ? (ecWeldingLen * weldPrice).toFixed(2)
          : "";
      } else {
        currentRow.welding_cost = "";
      }
      const weldingCostPerInch =
        parseFloat(currentRow.welding_cost_per_inch) || 0;
      const weldingSettingTimeCost =
        parseFloat(currentRow.welding_setting_time_cost) || 0;
      currentRow.total_welding_cost =
        weldingCostPerInch || ecWeldingLen || weldingSettingTimeCost
          ? (
              weldingCostPerInch * ecWeldingLen +
              weldingSettingTimeCost
            ).toFixed(2)
          : "";
      const fastnersCostPerNo =
        parseFloat(currentRow.fastners_cost_per_no) || 0;
      const quantity = parseFloat(currentRow.quantity) || 0;
      const pressingCost = parseFloat(currentRow.pressing_cost) || 0;
      const fastnersNumerator =
        fastnersCostPerNo * quantity + quantity * pressingCost;
      currentRow.total_fastners_cost = fastnersNumerator
        ? fastnersNumerator.toFixed(2)
        : "";
      const rawMtlCostValue = parseFloat(currentRow.raw_mtl_cost) || 0;
      const totalLaserPunchingCostValue =
        parseFloat(currentRow.total_laser_punching_cost) || 0;
      const totalBendingCostValue =
        parseFloat(currentRow.total_bending_cost) || 0;
      const totalWeldingCostValue =
        parseFloat(currentRow.total_welding_cost) || 0;
      const totalFastnersCostValue =
        parseFloat(currentRow.total_fastners_cost) || 0;
      const totalCoatingCostValue =
        parseFloat(currentRow.total_coating_cost) || 0;
      const totalProcessSum =
        rawMtlCostValue +
        totalLaserPunchingCostValue +
        totalBendingCostValue +
        totalWeldingCostValue +
        totalFastnersCostValue +
        totalCoatingCostValue;
      currentRow.total_process_cost = totalProcessSum
        ? totalProcessSum.toFixed(2)
        : "";
      const profitPercentage =
        parseFloat(formData.primaryDetails?.profit_percentage) || 0;
      currentRow.profict_cost =
        currentRow.total_process_cost && profitPercentage
          ? (
              parseFloat(currentRow.total_process_cost) *
              (profitPercentage / 100)
            ).toFixed(2)
          : "";
      const packingPercentage =
        parseFloat(formData.primaryDetails?.packing_percentage) || 0;
      currentRow.packing_cost =
        currentRow.total_process_cost && packingPercentage
          ? (
              parseFloat(currentRow.total_process_cost) *
              (packingPercentage / 100)
            ).toFixed(2)
          : "";
      const handlingPercentage =
        parseFloat(formData.primaryDetails?.handling_perncentage) || 0;
      currentRow.overhead_and_handling_cost =
        currentRow.total_process_cost && handlingPercentage
          ? (
              parseFloat(currentRow.total_process_cost) *
              (handlingPercentage / 100)
            ).toFixed(2)
          : "";
      currentRow.part_cost_per_no = (
        (parseFloat(currentRow.total_process_cost) || 0) +
        (parseFloat(currentRow.profict_cost) || 0) +
        (parseFloat(currentRow.packing_cost) || 0) +
        (parseFloat(currentRow.overhead_and_handling_cost) || 0)
      ).toFixed(2);
      currentRow.total_part_cost =
        currentRow.part_cost_per_no && qtyNo
          ? (parseFloat(currentRow.part_cost_per_no) * qtyNo).toFixed(2)
          : "";
    }

    currentRow.gross_amt = calculateGrossAmount(currentRow);

    updatedRows[index] = currentRow;
    setRows([...updatedRows]);
    setTransactionItems([...updatedRows]);
  };

  const validateRows = useCallback(
    (dataToValidate) => {
      const { tran_id, ...filteredPrimaryDetails } =
        formData.primaryDetails || {};
      for (const [key, value] of Object.entries(filteredPrimaryDetails)) {
        const field = headFields.find((field) => field.field_name === key);
        if (field && !value && field.is_required) {
          displayToast(`${field.title} is required`, "error");
          return false;
        }
      }

      for (const row of dataToValidate) {
        for (const field of allTableFields) {
          if (
            field.is_tax &&
            field.is_tax_percentage &&
            field.field_name.includes("_amt")
          ) {
            continue;
          }

          if (field.is_required && !row[field.field_name]) {
            displayToast(
              `${field.title || field.field_name} is required`,
              "error",
            );
            return false;
          }
        }
      }
      return true;
    },
    [allTableFields, formData.primaryDetails, headFields],
  );

  const validateJobCardSubmission = useCallback(
    (rowsWithSerialNo) => {
      const { primaryDetails } = formData;

      const mandatoryKeys = [
        "date",
        "total_quantity",
        "tran_no",
        "product_name",
      ];

      for (const key of mandatoryKeys) {
        if (
          primaryDetails[key] === null ||
          primaryDetails[key] === undefined ||
          primaryDetails[key] === ""
        ) {
          displayToast(`${key.replace(/_/g, " ")} is required`, "error");
          return false;
        }
      }

      for (const row of rowsWithSerialNo) {
        const itemMandatoryFields = [];

        for (const field of itemMandatoryFields) {
          if (!row[field]) {
            displayToast(
              `Item field "${field.replace(/_/g, " ")}" is required`,
              "error",
            );
            return false;
          }
        }

        if (row.jobcard_start && !row.allocated_work_station) {
          displayToast(
            "Allocated Workstation is required when Jobcard is started",
            "error",
          );
          return false;
        }

        if (
          row.jobcard_finish &&
          (row.time_taken === null || row.time_taken === "")
        ) {
          displayToast(
            "Time Required is required when Jobcard is finished",
            "error",
          );
          return false;
        }
      }

      return true;
    },
    [formData],
  );

  const handleSubmit = async () => {
    if (submitLock.current || isButtonLoading || isViewMode) return;
    if (draftSubmission && !draftSubmission.canAdd) {
      displayToast("Add permission is required to submit this entry", "error");
      return;
    }
    submitLock.current = true;
    let locked = false;
    try {
      if (draftSubmission) { draftSubmission.controller.beginSubmission(); locked = true; }
      await submitTransaction();
    } catch (error) {
      displayToast(error.message || "Failed to submit transaction", "error");
    } finally {
      if (locked) draftSubmission.controller.finishSubmission();
      submitLock.current = false;
    }
  };

  const submitTransaction = async () => {
    if (isViewMode) return;

    if (
      !isEditMode &&
      isAutoTransactionNo &&
      type !== "JobCard" &&
      !formData.primaryDetails?.tran_no
    ) {
      try {
        const tranNoResponse = await fetchTransactionNo(transactionCode);
        const fetchedTranNo = tranNoResponse?.TransactionNumber;
        if (fetchedTranNo) {
          formData.primaryDetails.tran_no = fetchedTranNo;
          setPrimaryDetails({ tran_no: fetchedTranNo });
        }
      } catch (error) {}
    }

    const rowsWithSerialNo = assignSerialNumbers(rows).map(
      ({ row, serialNo }) => {
        return {
          ...row,
          serial_no: serialNo,
          tran_code: transactionCode,
          tran_no: formData.primaryDetails?.tran_no || "",
        };
      },
    );

    if (!formData.primaryDetails.date) {
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];
      formData.primaryDetails.date = formattedDate;
    }

    if (type === "JobCard") {
      if (!validateJobCardSubmission(rowsWithSerialNo)) return;
      setIsButtonLoading(true);

      const submissionData = {
        tran_id: isEditMode ? formData.primaryDetails.tran_id : null,
        head_data: {
          ...formData.primaryDetails,
          tran_code: "JC",
          product_id: formData.primaryDetails?.product_id
            ? Number(formData.primaryDetails.product_id)
            : null,
          status:
            transactionData?.headDetails?.status === "started"
              ? "started"
              : transactionData?.headDetails?.status === "finished"
                ? "finished"
                : null,
        },
        item_data: rowsWithSerialNo.map((row) => {
          const { tran_no, tran_id, product_id, ...rest } = row;

          return {
            ...rest,
          };
        }),
      };

      try {
        const resultData = isEditMode
          ? await editJobCard(submissionData)
          : insertJobCard(submissionData);

        if (resultData.description || resultData.message) {
          handleErrorResponse(resultData.description || resultData.message);
          displayToast(resultData.description, "error");
          return;
        }
        displayToast(
          `JobCard ${isEditMode ? "Edited" : "Added"} successfully`,
          "success",
        );
        if (shouldNavigateAfterSubmit) {
          navigateToTransactionList();
        }
        resetTransactionTable();
        clearForm();
        return;
      } catch (error) {
        console.error("JobCard submission failed:", error);
        return;
      } finally {
        setIsButtonLoading(false);
      }
    }

    const totalQuantity = rowsWithSerialNo.reduce(
      (sum, row) => sum + parseFloat(row.quantity || 0),
      0,
    );

    const { subtotal, grossTotal } = calculateTotals();

    formData.primaryDetails.tran_code = transactionCode;
    formData.primaryDetails.total_quantity = totalQuantity;
    formData.primaryDetails.remaining_quantity = totalQuantity;
    formData.primaryDetails.net_amt = subtotal.toFixed(2);
    formData.primaryDetails.gross_amt = grossTotal.toFixed(2);

    if (!validateRows(rowsWithSerialNo)) return;
    setIsButtonLoading(true);

    const updatedRows = rowsWithSerialNo.map((row) => {
      if (row.selected && row.id) {
        const { selected, id, ...rest } = row;
        return {
          ...rest,
          remaining_quantity: row.quantity || 0,
        };
      }
      return {
        ...row,
        remaining_quantity: row.quantity || 0,
      };
    });

    try {
      const {
        tran_id,
        file_url,
        Date: capitalDate,
        ...filteredPrimaryDetails
      } = formData.primaryDetails;

      if (capitalDate && !filteredPrimaryDetails.date) {
        filteredPrimaryDetails.date = capitalDate;
      }

      if (transactionCode === "WO") {
        try {
          const pendingBomDraft = JSON.parse(
            sessionStorage.getItem("pendingWorkOrderBomDraft") || "null",
          );
          const currentWorkOrderNo =
            filteredPrimaryDetails.tran_no ||
            filteredPrimaryDetails.workorder_no ||
            filteredPrimaryDetails.wo_no;
          const isSameWorkOrder =
            pendingBomDraft?.workOrderNo &&
            currentWorkOrderNo &&
            pendingBomDraft.workOrderNo === currentWorkOrderNo;

          if (isSameWorkOrder) {
            if (!filteredPrimaryDetails.bom_number) {
              filteredPrimaryDetails.bom_number = pendingBomDraft.bom_number;
            }
            if (!filteredPrimaryDetails.bom && pendingBomDraft.bom) {
              filteredPrimaryDetails.bom = pendingBomDraft.bom;
            }
          }
        } catch (error) {}
      }

      const { country, pincode, ...filteredCustomerDetails } =
        formData.customerDetails;
      const uniqueOtherDetails = Array.isArray(otherDetailsData)
        ? [
            ...new Map(
              otherDetailsData.map((item) => [JSON.stringify(item), item]),
            ).values(),
          ]
        : [];

      const fileUploadItemFields = allTableFields
        .filter((field) => {
          const normalizedFieldType = String(field.field_type || "")
            .trim()
            .toLowerCase();
          const normalizedDataType = String(field.data_type || "")
            .trim()
            .toLowerCase();
          return (
            normalizedFieldType === "file_upload" ||
            normalizedFieldType === "case_file" ||
            normalizedDataType === "file_upload" ||
            normalizedDataType === "case_file" ||
            field.field_name === "inspection_report"
          );
        })
        .map((field) => field.field_name);
      const fileUploadHeadFields = headFields
        .filter((field) => {
          const fieldType = String(field.field_type || "")
            .trim()
            .toLowerCase();
          const dataType = String(field.data_type || "")
            .trim()
            .toLowerCase();
          return fieldType === "file_upload" || dataType === "file_upload";
        })
        .map((field) => field.field_name);

      fileUploadHeadFields.forEach((fieldName) => {
        const fieldValue = filteredPrimaryDetails[fieldName];
        if (typeof fieldValue !== "string" || !fieldValue) {
          delete filteredPrimaryDetails[fieldName];
        }
      });

      const submissionData = {
        tran_id: isEditMode ? formData.primaryDetails.tran_id : null,
        company_data: {
          ...filteredCustomerDetails,
        },
        head_data: {
          ...filteredPrimaryDetails,
          ...uniqueOtherDetails.reduce((acc, obj) => ({ ...acc, ...obj }), {}),
          file_url: file_url,
        },

        ...(!isEditMode &&
        locationState?.sourceTranCode &&
        locationState?.sourceTranId
          ? {
              source_tran_code: locationState.sourceTranCode,
              source_tran_id: Number(locationState.sourceTranId),
            }
          : {}),
        item_data: updatedRows.map((row) => {
          const itemRow = {
            ...row,
            product_id: row.product_id ? Number(row.product_id) : null,
            product_name: row.product_name || row.Product_Name || "",
          };

          fileUploadItemFields.forEach((fieldName) => {
            delete itemRow[`${fieldName}_name`];
          });

          if (!itemRow.inspection_report) {
            delete itemRow.inspection_report;
          }
          return {
            ...itemRow,
          };
        }),
      };

      if (draftSubmission) {
        Object.assign(submissionData, await draftSubmission.controller.prepareSubmission());
      }
      const resultData = isEditMode
        ? await editTransactionByCode(submissionData, transactionCode)
        : draftSubmission
          ? await insertDraftTransaction(submissionData, transactionCode)
          : await insertTransactionByCode(submissionData, transactionCode);

      if (resultData.description || resultData.error) {
        handleErrorResponse(resultData.description || resultData.error?.description);
        displayToast(resultData.description || resultData.error?.description, "error");
        return;
      }

      // Only verified success from the strict NEW generic insert consumes the local draft.
      draftSubmission?.controller.commit();

      if (
        !isEditMode &&
        locationState?.sourceTranCode &&
        locationState?.sourceTranId &&
        locationState?.sourceCategory &&
        tab
      ) {
        sessionStorage.setItem(
          `clearCache_${tab}_${locationState.sourceCategory}`,
          "true",
        );
      }

      if (!isEditMode && stagedAttachments.length) {
        const savedTransactionId =
          resultData.tran_id ||
          resultData.head_data?.tran_id ||
          resultData.data?.tran_id ||
          resultData.data?.head_data?.tran_id ||
          resultData.item_data?.[0]?.tran_id ||
          resultData.data?.item_data?.[0]?.tran_id;

        if (!savedTransactionId) {
          displayToast(
            "Transaction was saved, but uploaded files could not be linked",
            "error",
          );
        } else {
          const attachmentsByScope = stagedAttachments.reduce(
            (groups, attachment) => {
              const key = attachment.scope || "head";
              const attachmentId = attachment.id ?? attachment.attachment_id;
              if (attachmentId != null) {
                groups[key] = [...(groups[key] || []), attachmentId];
              }
              return groups;
            },
            {},
          );

          try {
            await Promise.all(
              Object.entries(attachmentsByScope).map(([scope, attachmentIds]) =>
                adoptAttachments({
                  attachmentIds,
                  tranCode: transactionCode,
                  scope,
                  parentId: savedTransactionId,
                }),
              ),
            );
            clearStagedAttachments();
          } catch (error) {
            console.error("Failed to adopt staged attachments:", error);
            displayToast(
              "Transaction was saved, but some uploaded files could not be linked",
              "error",
            );
          }
        }
      }

      if (transactionCode === "WO") {
        sessionStorage.removeItem("pendingWorkOrderBomDraft");
      }

      if (transactionCode === "WBOM" && !isEditMode) {
        const woTranId = formData.primaryDetails?.wo_tran_id;
        const wbomTranId =
          resultData.tran_id || resultData.item_data?.[0]?.tran_id;
        const wbomTranNo =
          formData.primaryDetails?.tran_no ||
          resultData.head_data?.tran_no ||
          resultData.data?.head_data?.tran_no;
        if (woTranId) {
          try {
            if (wbomTranNo) {
              sessionStorage.setItem(
                `workOrderBomNumber_${woTranId}`,
                String(wbomTranNo),
              );
            }

            if (wbomTranId) {
              await updateWbomTranIdInWO({
                wo_tran_id: Number(woTranId),
                wbom_tran_id: Number(wbomTranId),
                bom_number: wbomTranNo,
              });
            }

            if (wbomTranNo) {
              const woHeadDetails = locationState?.primaryDetails || {};
              const woItems = locationState?.transactionItems || [];
              const woCompanyDetails = locationState?.customerDetails || {};

              await editTransactionByCode(
                {
                  tran_id: Number(woTranId),
                  company_data: woCompanyDetails,
                  head_data: {
                    ...woHeadDetails,
                    tran_id: Number(woTranId),
                    tran_code: "WO",
                    ...(wbomTranId ? { bom: String(wbomTranId) } : {}),
                    bom_number: wbomTranNo,
                  },
                  item_data: woItems,
                },
                "WO",
              );
            }
          } catch (err) {
            console.error("Failed to update WBOM details in WO:", err);
          }
        }
      }

      clearOtherDetails();
      resetTransactionTable();
      clearForm();
      clearTransactionState();

      if (shouldNavigateAfterSubmit) {
        if (transactionCode === "WBOM") {
          const savedWbomTranId =
            resultData.tran_id ||
            resultData.head_data?.tran_id ||
            resultData.data?.tran_id ||
            resultData.data?.head_data?.tran_id ||
            resultData.item_data?.[0]?.tran_id ||
            resultData.data?.item_data?.[0]?.tran_id;
          const savedWbomTranNo =
            formData.primaryDetails?.tran_no ||
            resultData.head_data?.tran_no ||
            resultData.data?.head_data?.tran_no ||
            resultData.data?.tran_no;

          navigateToWorkOrderDetail(savedWbomTranNo, savedWbomTranId);
        } else if (transactionCode === "PL") {
          navigateToParentTransactionDetail();
        } else {
          navigateToTransactionList();
        }
      }

      displayToast(
        `Transaction ${isEditMode ? "Edited" : "Added"} successfully`,
        "success",
      );
    } catch (error) {
      console.error("Error submitting transaction:", error);
      draftSubmission?.controller.failSubmission(error);
      displayToast(error.message || "Failed to submit transaction", "error");
    } finally {
      setIsButtonLoading(false);
      setIsEditMode(false);
    }
  };

  const handleReviseQuotation = async () => {
    if (isViewMode || transactionCode !== "SQ" || isButtonLoading) return;

    const originalTranId = formData.primaryDetails?.tran_id;
    if (!originalTranId) {
      displayToast("Save the quotation before creating a revision", "error");
      return;
    }

    const rowsWithSerialNo = assignSerialNumbers(rows).map(
      ({ row, serialNo }) => {
        return {
          ...row,
          serial_no: serialNo,
          tran_code: transactionCode,
          tran_no: formData.primaryDetails?.tran_no || "",
        };
      },
    );

    if (!validateRows(rowsWithSerialNo)) return;
    setIsButtonLoading(true);

    try {
      const totalQuantity = rowsWithSerialNo.reduce(
        (sum, row) => sum + parseFloat(row.quantity || 0),
        0,
      );
      const { subtotal, grossTotal } = calculateTotals();

      const {
        tran_id,
        file_url,
        Date: capitalDate,
        ...filteredPrimaryDetails
      } = formData.primaryDetails;
      if (capitalDate && !filteredPrimaryDetails.date) {
        filteredPrimaryDetails.date = capitalDate;
      }
      filteredPrimaryDetails.tran_code = transactionCode;
      filteredPrimaryDetails.total_quantity = totalQuantity;
      filteredPrimaryDetails.remaining_quantity = totalQuantity;
      filteredPrimaryDetails.net_amt = subtotal.toFixed(2);
      filteredPrimaryDetails.gross_amt = grossTotal.toFixed(2);

      const { country, pincode, ...filteredCustomerDetails } =
        formData.customerDetails;

      const uniqueOtherDetails = Array.isArray(otherDetailsData)
        ? [
            ...new Map(
              otherDetailsData.map((detail) => [
                JSON.stringify(detail),
                detail,
              ]),
            ).values(),
          ]
        : [];

      const fileUploadItemFields = allTableFields
        .filter((field) => {
          const normalizedFieldType = String(field.field_type || "")
            .trim()
            .toLowerCase();
          const normalizedDataType = String(field.data_type || "")
            .trim()
            .toLowerCase();
          return (
            normalizedFieldType === "file_upload" ||
            normalizedFieldType === "case_file" ||
            normalizedDataType === "file_upload" ||
            normalizedDataType === "case_file" ||
            field.field_name === "inspection_report"
          );
        })
        .map((field) => field.field_name);

      const submissionData = {
        company_data: { ...filteredCustomerDetails },
        head_data: {
          ...filteredPrimaryDetails,
          ...uniqueOtherDetails.reduce(
            (acc, detail) => ({ ...acc, ...detail }),
            {},
          ),
          file_url: file_url,
        },
        item_data: rowsWithSerialNo.map((row) => {
          const { selected, id, ...rest } = row;
          const itemRow = {
            ...rest,
            product_id: row.product_id ? Number(row.product_id) : null,
            product_name: row.product_name || row.Product_Name || "",
            remaining_quantity: row.quantity || 0,
          };
          fileUploadItemFields.forEach((fieldName) => {
            delete itemRow[`${fieldName}_name`];
          });
          if (!itemRow.inspection_report) {
            delete itemRow.inspection_report;
          }
          return itemRow;
        }),
      };

      const result = await reviseQuotation(
        Number(originalTranId),
        submissionData,
      );

      if (result?.tran_id) {
        displayToast(
          `Revised quotation R${result.revision_no} created`,
          "success",
        );
        clearOtherDetails();
        resetTransactionTable();
        clearForm();
        clearTransactionState();
        setIsEditMode(false);
        navigate(`/${tab}/${category}/transactions`, {
          state: {
            transactionId: result.tran_id,
            transactionCode,
          },
        });
      } else {
        const message =
          result?.description ||
          result?.error?.description ||
          "Failed to create the revised quotation";
        handleErrorResponse(message);
        displayToast(message, "error");
      }
    } catch (error) {
      console.error("Error revising quotation:", error);
      displayToast("Failed to create the revised quotation", "error");
    } finally {
      setIsButtonLoading(false);
    }
  };

  const handleFileChange = async (
    e,
    index,
    fieldName = "inspection_report",
  ) => {
    const files = e.target.files;
    const file = files?.[0];
    if (!file) return;

    const fileUploadKey = `${index}-${fieldName}`;

    setFileUploadLoading((prev) => ({ ...prev, [fileUploadKey]: true }));

    try {
      const uploadResponse = await uploadFile(files, "transaction", {
        tranCode: transactionCode,
        scope: "item",
        fieldName,
        parentId: rows[index]?.tran_id,
      });

      if (uploadResponse.error) {
        displayToast(uploadResponse.error, "error");
        return;
      }

      const uploadedUrl = uploadResponse?.url;
      if (uploadedUrl) {
        setRows((previousRows) => {
          const updatedRows = [...previousRows];
          updatedRows[index] = {
            ...updatedRows[index],
            [fieldName]: uploadedUrl,
          };
          setTransactionItems(updatedRows);
          return updatedRows;
        });
      }

      if (!rows[index]?.tran_id && uploadResponse.attachments) {
        addStagedAttachments(
          uploadResponse.attachments.map((attachment) => ({
            ...attachment,
            scope: "item",
            fieldName,
            rowIndex: index,
          })),
        );
      }

      if (uploadResponse.attachments) {
        const attachmentKey = `${rows[index]?.tran_id || `new-${index}`}:${fieldName}`;
        setItemAttachmentsByField((previous) => ({
          ...previous,
          [attachmentKey]: [
            ...(previous[attachmentKey] || []),
            ...uploadResponse.attachments,
          ],
        }));
      }

      displayToast("File uploaded successfully", "success");
    } catch (error) {
      console.error("Error uploading file:", error);
      displayToast("Failed to upload file", "error");
    } finally {
      setFileUploadLoading((prev) => ({ ...prev, [fileUploadKey]: false }));
    }
  };

  const handleRemovePendingItemAttachment = async (
    index,
    fieldName,
    attachment,
  ) => {
    const attachmentId = attachment.id ?? attachment.attachment_id;
    const hasSavedItem = Boolean(rows[index]?.tran_id);

    if (attachmentId != null && hasSavedItem) {
      try {
        await deleteAttachment(attachmentId);
      } catch (error) {
        displayToast("Failed to delete file", "error");
        return;
      }
    } else if (attachmentId != null) {
      removeStagedAttachments([attachmentId]);
    }
    setRows((previousRows) => {
      const updatedRows = [...previousRows];
      updatedRows[index] = { ...updatedRows[index], [fieldName]: null };
      setTransactionItems(updatedRows);
      return updatedRows;
    });
    const attachmentKey = `${rows[index]?.tran_id || `new-${index}`}:${fieldName}`;
    setItemAttachmentsByField((previous) => ({
      ...previous,
      [attachmentKey]: (previous[attachmentKey] || []).filter(
        (currentAttachment) =>
          (currentAttachment.id ?? currentAttachment.attachment_id) !==
          attachmentId,
      ),
    }));
  };

  const formatSerialNo = (serialNo) => {
    if (!serialNo) return "";
    return serialNo.toString().startsWith("SN-") ? serialNo : `SN-${serialNo}`;
  };

  const resolveJobCardTranId = (response) => {
    const candidates = [
      Array.isArray(response?.item_data)
        ? response.item_data[0]?.tran_id
        : null,
      Array.isArray(response?.item_data)
        ? response.item_data[0]?.job_card_id
        : null,
      Array.isArray(response?.item_data)
        ? response.item_data[0]?.jc_tran_id
        : null,
      Array.isArray(response?.data?.item_data)
        ? response.data.item_data[0]?.tran_id
        : null,
      Array.isArray(response?.data?.item_data)
        ? response.data.item_data[0]?.job_card_id
        : null,
      Array.isArray(response?.data?.item_data)
        ? response.data.item_data[0]?.jc_tran_id
        : null,
      response?.tran_id,
      response?.job_card_id,
      response?.jc_tran_id,
      response?.data?.tran_id,
      response?.data?.job_card_id,
      response?.data?.jc_tran_id,
      response?.head_data?.tran_id,
      response?.head_data?.job_card_id,
      response?.head_data?.jc_tran_id,
      response?.data?.head_data?.tran_id,
      response?.data?.head_data?.job_card_id,
      response?.data?.head_data?.jc_tran_id,
    ];

    const found = candidates.find(
      (value) => value !== null && value !== undefined && value !== "",
    );
    return found ? Number(found) : null;
  };

  const resolveJobCardTranIdFromList = async (row, rowIndex = 0) => {
    try {
      const response = await fetchTransactionByCode({
        transactionCode: "JC",
        page: 1,
        pageSize: 5000,
      });

      const list =
        response?.TransactionList ||
        response?.items ||
        response?.data?.TransactionList ||
        [];

      if (!Array.isArray(list) || list.length === 0) return null;

      const normalizeText = (value) =>
        String(value || "")
          .trim()
          .toLowerCase();

      const woTranId = Number(
        formData?.primaryDetails?.tran_id ||
          transactionData?.headDetails?.tran_id,
      );
      const woNo = normalizeText(
        formData?.primaryDetails?.workorder_no ||
          formData?.primaryDetails?.wo_no ||
          formData?.primaryDetails?.tran_no ||
          transactionData?.headDetails?.workorder_no ||
          transactionData?.headDetails?.wo_no ||
          transactionData?.headDetails?.tran_no,
      );
      const rowSerialNo = Number(row?.serial_no || rowIndex + 1);
      const rowProductId = Number(row?.product_id || 0);
      const rowProductName = normalizeText(
        row?.product_name || row?.Product_Name || row?.name,
      );

      const scoredMatches = list
        .map((item) => {
          const itemTranId = Number(
            item?.tran_id || item?.id || item?.jc_tran_id || 0,
          );
          if (!itemTranId) return null;

          const refWoTranId = Number(
            item?.ref_tran_id ||
              item?.wo_tran_id ||
              item?.workorder_tran_id ||
              0,
          );
          const refSerialNo = Number(
            item?.ref_item_serial_no ||
              item?.ref_serial_no ||
              item?.ref_serial ||
              item?.job_card_serial_no ||
              item?.jc_serial_no ||
              item?.serial_no ||
              0,
          );
          const itemProductId = Number(item?.product_id || 0);
          const itemWoNo = normalizeText(
            item?.workorder_no || item?.wo_no || "",
          );
          const itemNarration = normalizeText(item?.narration || "");
          const itemProductName = normalizeText(
            item?.product_name || item?.Product_Name || "",
          );

          const hasWoTranIdMatch = woTranId > 0 && refWoTranId === woTranId;
          const hasWoNoMatch =
            Boolean(woNo) &&
            ((itemWoNo && itemWoNo === woNo) || itemNarration.includes(woNo));
          const hasSerialMatch =
            rowSerialNo > 0 && refSerialNo > 0 && refSerialNo === rowSerialNo;
          const hasProductIdMatch =
            rowProductId > 0 &&
            itemProductId > 0 &&
            itemProductId === rowProductId;
          const hasProductNameMatch =
            rowProductName &&
            itemProductName &&
            itemProductName === rowProductName;
          if (!hasWoTranIdMatch && !hasWoNoMatch) {
            return null;
          }
          if (!hasSerialMatch && !hasProductIdMatch && !hasProductNameMatch) {
            return null;
          }

          let score = 0;

          if (hasWoTranIdMatch) score += 25;
          if (woNo && itemWoNo && itemWoNo === woNo) score += 20;
          if (woNo && itemNarration.includes(woNo)) score += 12;
          if (hasSerialMatch) score += 40;
          if (hasProductIdMatch) score += 30;
          if (hasProductNameMatch) score += 15;

          return score > 0 ? { item, score, itemTranId } : null;
        })
        .filter(Boolean);

      const sortedMatches = scoredMatches.sort(
        (a, b) =>
          b.score - a.score ||
          Number(b?.itemTranId || 0) - Number(a?.itemTranId || 0),
      );

      const found = sortedMatches[0]?.item;
      return found
        ? Number(found?.tran_id || found?.id || found?.jc_tran_id || 0) || null
        : null;
    } catch (error) {
      return null;
    }
  };

  const handleCreateJobcard = async (index, row) => {
    if (!isViewMode && !isEditMode) {
      let errorMessage =
        "Please create a transaction first before creating Jobcard.";
      if (isCopyMode) {
        errorMessage = "Cannot create Jobcard in Copy Mode.";
      }
      displayToast(errorMessage, "error");
      return;
    }

    try {
      const updatedRows = [...rows];
      updatedRows[index] = { ...updatedRows[index] };
      setRows(updatedRows);

      const requestBody = {
        tran_id: row.tran_id,
        serial_no: row.serial_no || index + 1,
      };

      const response = await createJobcard(requestBody);
      if (response.status === "success") {
        const successRows = [...rows];
        successRows[index] = {
          ...successRows[index],
          create_jobcard: "success",
        };
        setRows(successRows);

        displayToast("Jobcard created successfully", "success");
      } else {
        const errorRows = [...rows];
        errorRows[index] = { ...errorRows[index] };
        setRows(errorRows);

        displayToast(response.description, "error");
      }
    } catch (error) {
      const errorRows = [...rows];
      errorRows[index] = { ...errorRows[index] };
      setRows(errorRows);

      displayToast(
        "An error occurred while creating jobcard. Please try again.",
        "error",
      );
    }
  };

  const handleCreateBom = (indexOrRow, rowArg) => {
    let targetRow = null;

    if (rowArg && typeof rowArg === "object") {
      targetRow = rowArg;
    } else if (typeof indexOrRow === "number") {
      targetRow =
        (formData?.transactionItems && formData.transactionItems[indexOrRow]) ||
        rows[indexOrRow];
    } else if (indexOrRow && typeof indexOrRow === "object") {
      targetRow = indexOrRow;
    } else {
      targetRow =
        (formData?.transactionItems && formData.transactionItems[0]) || rows[0];
    }

    if (!targetRow) {
      displayToast("No product selected for BOM", "error");
      return;
    }

    const productData = {
      name:
        targetRow.product_name ||
        targetRow.Product_Name ||
        targetRow.name ||
        "",
      description:
        targetRow.product_description ||
        targetRow.description ||
        targetRow.product_desc ||
        "",
      tran_id:
        targetRow.product_id ||
        targetRow.tran_id ||
        (targetRow.id ? Number(targetRow.id) : null),
      bom_created: "success",
      item: targetRow,
    };

    navigate(`/${tab}/${category}/bom`, {
      state: {
        productData,
        isViewMode:
          productData.bom_created === "success" ||
          formData?.bom_created === true,
      },
    });
  };

  const handleCreatePackingList = (indexOrRow, rowArg) => {
    let targetRow = null;

    if (rowArg && typeof rowArg === "object") {
      targetRow = rowArg;
    } else if (typeof indexOrRow === "number") {
      targetRow =
        (formData?.transactionItems && formData.transactionItems[indexOrRow]) ||
        rows[indexOrRow];
    } else if (indexOrRow && typeof indexOrRow === "object") {
      targetRow = indexOrRow;
    } else {
      targetRow =
        (formData?.transactionItems && formData.transactionItems[0]) || rows[0];
    }

    if (!targetRow) {
      displayToast("No product selected for Packing List", "error");
      return;
    }

    const productData = {
      name:
        targetRow.product_name ||
        targetRow.Product_Name ||
        targetRow.name ||
        "",
      description:
        targetRow.product_description ||
        targetRow.description ||
        targetRow.product_desc ||
        "",
      tran_id:
        targetRow.product_id ||
        targetRow.tran_id ||
        (targetRow.id ? Number(targetRow.id) : null),
      pl_created: targetRow.packing_list === "success" ? "success" : undefined,
      item: targetRow,
    };

    const woNo =
      formData?.primaryDetails?.tran_no ||
      transactionData?.headDetails?.tran_no ||
      targetRow?.tran_no ||
      "";

    navigate(`/${tab}/${category}/packing-list`, {
      state: {
        productData: productData,
        isViewMode:
          productData.pl_created === "success" || formData?.pl_created === true,
        woNo,
        fromTransactionDetail: true,
        returnTransactionId:
          formData?.primaryDetails?.tran_id ||
          transactionData?.headDetails?.tran_id ||
          null,
        returnTransactionCode: transactionCode,
        returnLocationState: locationState,
      },
    });
  };

  const formatFieldValue = (value, fieldType, rowId, onFileChange, row) => {
    if (value === null || value === undefined || value === "") {
      if (fieldType === "file_upload" || fieldType === "case_file") {
        return <span className="no-file-text">No file uploaded</span>;
      }
      if (isCreateJobCardType(fieldType)) {
        if (transactionCode === "WO") {
          return renderWorkOrderCardButton(row, rowId);
        }

        const isJobcardCreated =
          row?.create_jobcard === "success" || row?.[fieldType] === "success";

        return isJobcardCreated ? (
          <span>Created</span>
        ) : (
          <button
            onClick={() => handleCreateJobcard(rowId)}
            style={{
              cursor: !isViewMode && !isEditMode ? "not-allowed" : "pointer",
            }}
          >
            Create Jobcard
          </button>
        );
      }

      if (fieldType === "BOM") {
        const isBomCreated =
          row?.bom === "success" || row?.[fieldType] === "success";

        if (isBomCreated) {
          return <span>Created</span>;
        }

        return (
          <button
            onClick={() => {
              handleCreateBom(rowId);
            }}
          >
            Bom
          </button>
        );
      }

      if (fieldType === "PACKING_LIST") {
        const isPackingListCreated =
          row?.bom === "success" || row?.[fieldType] === "success";

        if (isPackingListCreated) {
          return <span>Created</span>;
        }

        return (
          <button
            onClick={() => {
              handleCreatePackingList(rowId);
            }}
          >
            Packing List
          </button>
        );
      }
      return "-";
    }

    switch (fieldType) {
      case "decimal":
        return parseFloat(value).toFixed(2);
      case "datetime":
      case "date":
        return value ? new Date(value).toLocaleDateString() : "-";
      case "file_upload":
      case "case_file":
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="file-link"
          >
            View File
          </a>
        );
      case "CREATE_JOBCARD":
      case "CREATE_JOB_CARD":
        if (transactionCode === "WO") {
          return renderWorkOrderCardButton(row, rowId);
        }

        const isJobcardCreated =
          value === "success" || row?.create_jobcard === "success";

        return isJobcardCreated ? (
          <span>Created</span>
        ) : (
          <button
            onClick={() => handleCreateJobcard(rowId)}
            style={{
              cursor: !isViewMode && !isEditMode ? "not-allowed" : "pointer",
            }}
          >
            Create Jobcard
          </button>
        );
      case "ROUTE_CARD":
        const isRouteCardCreated = isRouteCardGreenForRow(row);
        return (
          <button
            onClick={() => handleRouteCardClick(row, rowId)}
            className="route-card-btn"
            style={{
              cursor: "pointer",
              color: isRouteCardCreated ? "#4caf50" : "inherit",
            }}
          >
            Route Card
          </button>
        );
      default:
        return value.toString();
    }
  };

  const handleJobcardAction = async (fieldName, row, index) => {
    try {
      const item = formData?.transactionItems?.[index];

      if (fieldName === "jobcard_start") {
        if (jobCardStatus !== "started") {
          displayToast(
            "Jobcard process only starts when the jobcard is started.",
            "error",
          );
          return;
        }

        if (!row.allocated_work_station) {
          displayToast(
            "Please add and save the allocated work station to start the jobcard process for this item.",
            "error",
          );
          return;
        }

        if (!item?.tran_id) {
          displayToast(
            "Please save the process before starting the jobcard process.",
            "error",
          );
          return;
        }
      }

      if (fieldName === "jobcard_finish") {
        if (jobCardStatus !== "started") {
          displayToast(
            "Jobcard must be started before finishing the process.",
            "error",
          );
          return;
        }

        if (row.jobcard_start !== "started") {
          displayToast("Jobcard process not started for this item", "error");
          return;
        }

        if (!row.time_taken) {
          displayToast(
            "Please add and save the Time taken  to finish jobcard process",
            "error",
          );
          return;
        }

        if (!item?.tran_id) {
          displayToast(
            "Please save the process before finishing the jobcard process.",
            "error",
          );
          return;
        }
      }

      if (
        fieldName === "jobcard_start" &&
        (item?.jobcard_start === "started" ||
          rows?.[index]?.jobcard_start === "started")
      ) {
        const updateData = {
          ...formData,
          transactionItems: formData?.transactionItems?.map((it, i) =>
            i === index ? { ...it, jobcard_start: "" } : it,
          ),
        };

        try {
          const result = await editJobCard({
            tran_id: formData.primaryDetails.tran_id,
            head_data: {
              ...formData.primaryDetails,
              tran_code: "JC",
              product_id: formData.primaryDetails?.product_id
                ? Number(formData.primaryDetails.product_id)
                : null,
              status:
                transactionData?.headDetails?.status === "started"
                  ? "started"
                  : transactionData?.headDetails?.status === "finished"
                    ? "finished"
                    : null,
            },
            item_data: updateData.transactionItems.map(
              ({ tran_no, tran_id, product_id, ...rest }) => rest,
            ),
          });

          if (result?.description || result?.message) {
            displayToast(result.description || result.message);
          } else {
            const updatedRows = rows.map((r, i) =>
              i === index ? { ...r, jobcard_start: "" } : r,
            );
            setRows(updatedRows);
            setTransactionItems(updatedRows);
            displayToast("Jobcard process updated locally", "success");
          }
        } catch (error) {
          displayToast("Failed to update jobcard", "error");
        }

        return;
      }

      const status = fieldName === "jobcard_finish" ? "finish" : "start";
      const jobCardData = {
        tran_id: row.tran_id || formData.primaryDetails.tran_id,
        serial_no: row.serial_no || index + 1,
        [fieldName === "jobcard_finish"
          ? "time_taken"
          : "allocated_work_station"]:
          fieldName === "jobcard_finish"
            ? parseInt(row.time_taken, 10) || null
            : row.allocated_work_station,
      };

      const result = await updateJobCardProcessStatus(status, jobCardData);
      if (result?.description || result.message) {
        displayToast(
          result?.description ||
            result.message ||
            "An unexpected error occurred while updating the jobcard process",
          "error",
        );
        return;
      }
      let statusMessage = "";
      let updatedStatus = "";

      if (fieldName === "jobcard_start") {
        statusMessage = "Job card process started successfully";
        updatedStatus = "started";
      } else if (fieldName === "jobcard_finish") {
        statusMessage = "Job card finished process successfully";
        updatedStatus = "finished";
      }

      if (result?.status === "success") {
        displayToast(statusMessage, "success");
        const updatedRow = {
          ...row,
          [fieldName]: updatedStatus,
        };

        const updatedRows = rows.map((r, i) => (i === index ? updatedRow : r));
        setRows(updatedRows);
        setTransactionItems(updatedRows);
      }
    } finally {
    }
  };

  const renderFieldContent = (row, field, index) => {
    const value = row[field.field_name];
    const normalizedFieldType = String(field.field_type || "").toLowerCase();
    const normalizedDataType = String(field.data_type || "").toLowerCase();
    const isFileUploadField =
      normalizedDataType === "file_upload" ||
      normalizedDataType === "case_file" ||
      normalizedFieldType === "file_upload" ||
      normalizedFieldType === "case_file" ||
      field.field_name === "inspection_report";
    const isEcCalculatedField =
      transactionCode === "EC" && EC_CALCULATED_FIELDS.has(field.field_name);
    const isEcHighlightField =
      transactionCode === "EC" &&
      (EC_CALCULATED_FIELDS.has(field.field_name) ||
        field.field_name === "item_weight_kg");
    const shouldExpandHorizontal =
      field.expand_horizontal === true ||
      field.expand_horizontal === 1 ||
      field.expand_horizontal === "1";
    const expandableInputStyle = shouldExpandHorizontal
      ? {
          width: `${Math.max(
            14,
            String(value || field.title || field.field_name || "").length + 2,
          )}ch`,
          minWidth: "140px",
        }
      : undefined;

    if (field.is_internal_field) {
      return (
        <input
          type="hidden"
          name={`${field.field_name}-${index}`}
          value={value || ""}
          onChange={(e) =>
            handleInputChange(index, field.field_name, e.target.value)
          }
        />
      );
    }

    if (isCreateJobCardField(field)) {
      if (transactionCode === "WO") {
        return (
          <div className={isViewMode ? "view-mode-cell" : ""}>
            {renderWorkOrderCardButton(row, index)}
          </div>
        );
      }

      const isJobcardCreated =
        row?.create_jobcard === "success" ||
        row?.[field.field_type] === "success";

      if (isViewMode) {
        return (
          <div className="view-mode-cell">
            {isJobcardCreated ? (
              <span>Created</span>
            ) : (
              <button
                onClick={() => handleCreateJobcard(index, row)}
                style={{
                  cursor:
                    !isViewMode && !isEditMode ? "not-allowed" : "pointer",
                }}
              >
                Create Jobcard
              </button>
            )}
          </div>
        );
      }
    }

    if (field.field_type === "BOM") {
      const isbomCreated =
        row?.bom === "success" || row?.[field.field_type] === "success";

      if (isViewMode) {
        return (
          <div className="view-mode-cell">
            {isbomCreated ? (
              <span>Created</span>
            ) : (
              <button
                onClick={() => handleCreateBom(index, row)}
                style={{
                  cursor:
                    !isViewMode && !isEditMode ? "not-allowed" : "pointer",
                }}
              >
                BOM
              </button>
            )}
          </div>
        );
      }
    }

    if (field.field_type === "PACKING_LIST") {
      const isPackingListCreated =
        row?.packing_list === "success" ||
        row?.[field.field_type] === "success";

      if (isViewMode) {
        return (
          <div className="view-mode-cell">
            {isPackingListCreated ? (
              <span>Created</span>
            ) : (
              <button
                onClick={() => handleCreatePackingList(index, row)}
                style={{
                  cursor:
                    !isViewMode && !isEditMode ? "not-allowed" : "pointer",
                }}
              >
                Packing List
              </button>
            )}
          </div>
        );
      }
    }

    if (field.field_type === "ROUTE_CARD") {
      const isRouteCardCreated = isRouteCardGreenForRow(row);

      return (
        <div className={isViewMode ? "view-mode-cell" : ""}>
          <button
            onClick={() => handleRouteCardClick(row, index)}
            className="route-card-btn"
            style={{
              cursor: "pointer",
              color: isRouteCardCreated ? "#4caf50" : "inherit",
            }}
          >
            Route Card
          </button>
        </div>
      );
    }

    if (field.field_name === "gross_amt") {
      return <div className="view-mode-cell">{value || "0.00"}</div>;
    }

    if (field.field_name === "amount") {
      return <div className="view-mode-cell">{value || "0.00"}</div>;
    }

    const calculatedQualityFields = [
      "copq_defect_reject",
      "copq_defect",
      "copq_defe",
      "copq_rework",
      "total_copq",
      "balance_quantity",
      "balance_qty",
      "delivered",
      "otif",
    ];

    const calculatedGrnFields = ["rejected_qty", "po_balance"];

    if (
      calculatedQualityFields.includes(field.field_name) ||
      (transactionCode === "GRN" &&
        calculatedGrnFields.includes(field.field_name))
    ) {
      if (field.field_name === "otif") {
        const otifValue = computeOtif(row);
        return <div className="view-mode-cell">{otifValue}</div>;
      } else if (field.field_name === "delivered") {
        return (
          <div className="view-mode-cell">{value ? `${value}%` : "0.00%"}</div>
        );
      } else {
        const isPendingGrnRejectedQuantity =
          transactionCode === "GRN" &&
          field.field_name === "rejected_qty" &&
          (row.accepted_qty === "" ||
            row.accepted_qty === null ||
            row.accepted_qty === undefined);
        return (
          <div className="view-mode-cell">
            {isPendingGrnRejectedQuantity ? "0.00" : value || "0.00"}
          </div>
        );
      }
    }

    if (field.field_name === "serial_no") {
      if (isViewMode) {
        return <div className="view-mode-cell">{formatSerialNo(value)}</div>;
      }
      return (
        <input
          type="text"
          className="custom-input"
          value={
            formatSerialNo(value) ||
            `SN-${assignSerialNumbers(rows)[index]?.serialNo || index + 1}`
          }
          disabled
        />
      );
    }

    if (isFileUploadField) {
      const fileInputRefKey = `${index}-${field.field_name}`;
      const attachmentKey = `${row.tran_id || `new-${index}`}:${field.field_name}`;
      const savedAttachments = filterAttachmentsForRowValue(
        itemAttachmentsByField[`${row.tran_id}:${field.field_name}`],
        value,
      );
      const pendingAttachments =
        (row.tran_id
          ? savedAttachments
          : itemAttachmentsByField[attachmentKey]) ||
        stagedAttachments.filter(
          (attachment) =>
            attachment.scope === "item" &&
            attachment.rowIndex === index &&
            attachment.fieldName === field.field_name,
        );
      const attachments = isViewMode ? savedAttachments : pendingAttachments;

      if (isViewMode) {
        return (
          <div className="attachment-list attachment-list--view">
            {attachments.length ? (
              attachments.map((attachment) => {
                const url =
                  attachment.url || attachment.file_url || attachment.s3_url;
                return (
                  <div
                    className="attachment-list__item"
                    key={attachment.id || attachment.attachment_id}
                  >
                    <FileText size={16} />
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="file-link"
                    >
                      {attachment.file_name || attachment.name || "View File"}
                    </a>
                  </div>
                );
              })
            ) : (
              <span className="attachment-list__empty">No files attached</span>
            )}
          </div>
        );
      }

      return (
        <div
          className={`file-upload-container ${
            attachments.length ? "file-upload-container--has-attachments" : ""
          }`}
        >
          <input
            type="file"
            accept="*/*"
            multiple
            id={`file-input-${field.field_name}-${index}`}
            style={{ display: "none" }}
            ref={(el) => (fileInputRefs.current[fileInputRefKey] = el)}
            onChange={(e) => handleFileChange(e, index, field.field_name)}
            disabled={fileUploadLoading[fileInputRefKey]}
          />
          <button
            type="button"
            className="choose-file-btn"
            onClick={() => fileInputRefs.current[fileInputRefKey]?.click()}
            disabled={fileUploadLoading[fileInputRefKey]}
          >
            {fileUploadLoading[fileInputRefKey]
              ? "Uploading..."
              : "Choose File"}
          </button>
          {attachments.length > 0 && (
            <div className="attachment-list attachment-list--pending">
              {attachments.map((attachment, attachmentIndex) => (
                <div
                  className="attachment-list__item"
                  key={
                    attachment.id ||
                    attachment.attachment_id ||
                    `${attachment.file_name}-${attachmentIndex}`
                  }
                >
                  <FileText size={16} />
                  <span
                    className="attachment-list__name"
                    title={attachment.file_name || attachment.name}
                  >
                    {attachment.file_name || attachment.name || "File uploaded"}
                  </span>
                  <button
                    type="button"
                    className="attachment-list__remove"
                    title="Remove this file"
                    onClick={() =>
                      handleRemovePendingItemAttachment(
                        index,
                        field.field_name,
                        attachment,
                      )
                    }
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (
      field.field_type === "BOOLEAN" ||
      field.field_type === "boolean" ||
      field.data_type === "boolean"
    ) {
      const isDisabled =
        isViewMode || (isEditMode && field.is_read_only_on_edit);

      const booleanValue =
        value === true ||
        value === 1 ||
        value === "1" ||
        (typeof value === "string" && value.toLowerCase() === "true");

      if (isViewMode) {
        return (
          <div className="view-mode-cell">
            <input
              type="checkbox"
              checked={booleanValue}
              disabled
              className="master-data-modal-checkbox"
            />
          </div>
        );
      }

      return (
        <input
          type="checkbox"
          className="master-data-modal-checkbox"
          checked={booleanValue}
          onChange={(e) => {
            handleInputChange(index, field.field_name, e.target.checked);
          }}
          disabled={isDisabled}
        />
      );
    }

    if (
      isViewMode &&
      field.field_name !== "jobcard_start" &&
      field.field_name !== "jobcard_finish"
    ) {
      const viewFieldType =
        normalizedDataType === "date" || normalizedFieldType === "date"
          ? "date"
          : normalizedDataType === "datetime" ||
              normalizedFieldType === "datetime"
            ? "datetime"
            : field.data_type;
      return (
        <div className="view-mode-cell">
          {formatFieldValue(value, viewFieldType)}
        </div>
      );
    }

    if (
      field.field_name === "jobcard_start" ||
      field.field_name === "jobcard_finish"
    ) {
      const isStart = field.field_name === "jobcard_start";
      const isStarted = row.jobcard_start === "started";
      const isFinished = row.jobcard_finish === "finished";

      const buttonText = isStart
        ? isStarted
          ? "Started"
          : "Start"
        : isFinished
          ? "Finished"
          : "Finish";

      return (
        <button
          className="jobcard-button"
          onClick={() => handleJobcardAction(field.field_name, row, index)}
          disabled={!isViewMode && !isEditMode}
          style={{
            cursor:
              (!isViewMode && !isEditMode) ||
              (isStart && isStarted && isFinished) ||
              (field.field_name !== "jobcard_start" && value === "finished")
                ? "not-allowed"
                : "pointer",
          }}
        >
          {buttonText}
        </button>
      );
    }

    const isDisabled =
      (isEditMode && field.is_read_only_on_edit) ||
      (field.is_tax && !field.is_tax_percentage) ||
      isEcCalculatedField;

    const isDateTimeField =
      normalizedDataType === "datetime" || normalizedFieldType === "datetime";
    const isDateOnlyField =
      !isDateTimeField &&
      (normalizedDataType === "date" || normalizedFieldType === "date");

    const effectiveSwitchType = isDateTimeField
      ? "datetime"
      : isDateOnlyField
        ? "date"
        : field.data_type;

    switch (effectiveSwitchType) {
      case "decimal":
      case "integer":
        return (
          <input
            type="number"
            step={field.data_type === "integer" ? "1" : "0.01"}
            className={`custom-input ${
              shouldExpandHorizontal ? "expand-horizontal-input" : ""
            } ${isEcHighlightField ? "ec-calculated-field" : ""}`}
            style={expandableInputStyle}
            placeholder={field.title}
            value={row[field.field_name] ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              if (field.data_type === "integer") {
                if (/^\d*$/.test(value)) {
                  handleInputChange(index, field.field_name, value);
                }
              } else {
                handleInputChange(index, field.field_name, value);
              }
            }}
            disabled={isDisabled}
            onClick={() =>
              !isEcCalculatedField && handleFieldClick(field, index)
            }
            onFocus={() =>
              !isEcCalculatedField && handleEnterValidation(field, index)
            }
            onBlur={() =>
              !isEcCalculatedField && handleLeaveValidation(field, index)
            }
          />
        );
      case "datetime":
        return (
          <input
            type="datetime-local"
            className={`custom-input ${
              shouldExpandHorizontal ? "expand-horizontal-input" : ""
            } ${isEcHighlightField ? "ec-calculated-field" : ""}`}
            style={expandableInputStyle}
            value={row[field.field_name] || ""}
            onChange={(e) =>
              handleInputChange(index, field.field_name, e.target.value)
            }
            onClick={() =>
              !isEcCalculatedField && handleFieldClick(field, index)
            }
            onFocus={() =>
              !isEcCalculatedField && handleEnterValidation(field, index)
            }
            onBlur={() =>
              !isEcCalculatedField && handleLeaveValidation(field, index)
            }
            disabled={isDisabled}
          />
        );
      case "date":
        return (
          <DatePicker
            selected={
              row[field.field_name] ? new Date(row[field.field_name]) : null
            }
            onChange={(date) => {
              if (isDisabled) return;
              handleInputChange(
                index,
                field.field_name,
                date ? date.toISOString().split("T")[0] : "",
              );
            }}
            dateFormat="dd-MM-yyyy"
            calendarContainer={(props) => (
              <DateCalendar
                {...props}
                onClear={
                  isDisabled
                    ? undefined
                    : () => handleInputChange(index, field.field_name, "")
                }
              />
            )}
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={26}
            dropdownMode="scroll"
            customInput={<AnalysisDateInput placeholder="Select a date" />}
            popperClassName="detail-datepicker-popper"
            popperPlacement="bottom-start"
            popperProps={{ strategy: "fixed" }}
            popperModifiers={CALENDAR_MODIFIERS}
            portalId="datepicker-portal"
            placeholderText="Select a date"
            disabled={isDisabled}
            onFocus={() =>
              !isEcCalculatedField && handleEnterValidation(field, index)
            }
            onBlur={() =>
              !isEcCalculatedField && handleLeaveValidation(field, index)
            }
          />
        );
      default:
        return (
          <input
            type={"text"}
            className={`custom-input ${
              shouldExpandHorizontal ? "expand-horizontal-input" : ""
            } ${isEcHighlightField ? "ec-calculated-field" : ""}`}
            style={expandableInputStyle}
            placeholder={field.title}
            value={row[field.field_name] || ""}
            onChange={(e) =>
              handleInputChange(index, field.field_name, e.target.value)
            }
            disabled={isDisabled}
            onClick={() =>
              !isEcCalculatedField && handleFieldClick(field, index)
            }
            onFocus={() =>
              !isEcCalculatedField && handleEnterValidation(field, index)
            }
            onBlur={() =>
              !isEcCalculatedField && handleLeaveValidation(field, index)
            }
          />
        );
    }
  };

  const { subtotal, grossTotal } = calculateTotals();
  const [showProductModal, setShowProductModal] = useState(false);
  const [showRouteCardPopup, setShowRouteCardPopup] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const hydratedWorkOrderRef = React.useRef(new Set());
  const effectiveTransactionCode = String(
    transactionCode ||
      transactionData?.headDetails?.tran_code ||
      formData?.primaryDetails?.tran_code ||
      "",
  )
    .trim()
    .toUpperCase();

  const productJustCreatedRef = React.useRef(false);
  const handleProductCreated = (createdProduct) => {
    if (!createdProduct) return;
    productJustCreatedRef.current = true;
    if (currentRowIndex === null || currentRowIndex === undefined) return;

    const productName =
      createdProduct.name ||
      createdProduct.product_name ||
      createdProduct.Product_Name ||
      "";
    const productId = createdProduct.tran_id
      ? Number(createdProduct.tran_id)
      : null;

    const updatedRows = [...rows];
    const currentRow = { ...updatedRows[currentRowIndex] };
    if (productName) {
      currentRow.product_name = productName;
    }
    currentRow.product_id = productId;
    if (
      createdProduct.uom &&
      (Object.prototype.hasOwnProperty.call(currentRow, "uom") ||
        allTableFields.some((field) => field.field_name === "uom"))
    ) {
      currentRow.uom = createdProduct.uom;
    }
    updatedRows[currentRowIndex] = currentRow;
    setRows(updatedRows);
    setTransactionItems(updatedRows);
    setIsPopupOpen(false);
  };

  const getRouteCardIdFromRow = (row) =>
    row?.route_card_id || row?.routecard_id || row?.route_card_head_id || null;

  const isTruthyRouteCardValue = (value) => {
    if (typeof value === "string") {
      const token = value.trim().toLowerCase();
      return ![
        "",
        "false",
        "0",
        "no",
        "null",
        "undefined",
        "not_created",
        "not created",
      ].includes(token);
    }

    return Boolean(value);
  };

  const isRouteCardCreatedForRow = (row) =>
    Boolean(
      getRouteCardIdFromRow(row) ||
      isTruthyRouteCardValue(row?.route_card_created) ||
      isTruthyRouteCardValue(row?.route_card) ||
      isTruthyRouteCardValue(row?.route_card_no),
    );

  useEffect(() => {
    const persistedTranId = Number(
      formData?.primaryDetails?.tran_id ||
        transactionData?.headDetails?.tran_id ||
        0,
    );
    const transactionKey = String(persistedTranId || "");

    if (
      effectiveTransactionCode !== "WO" ||
      !persistedTranId ||
      !transactionKey ||
      rows.length === 0 ||
      hydratedWorkOrderRef.current.has(transactionKey)
    ) {
      return;
    }

    const relevantRows = rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) =>
        Boolean(
          row?.product_id ||
          row?.product_name ||
          row?.Product_Name ||
          row?.serial_no ||
          row?.quantity,
        ),
      );

    if (!relevantRows.length) {
      hydratedWorkOrderRef.current.add(transactionKey);
      return;
    }

    let isActive = true;

    const hydrate = async () => {
      const nextRows = [...rows];
      let hasChanges = false;

      for (const { row, index } of relevantRows) {
        if (isRouteCardCreatedForRow(row)) {
          continue;
        }

        const resolvedJcTranId = await resolveJobCardTranIdFromList(row, index);
        if (!resolvedJcTranId) {
          continue;
        }

        try {
          const jcDetailResponse = await fetchTransactionByCodeAndId(
            "JC",
            resolvedJcTranId,
          );
          const jcHead = jcDetailResponse?.head_data || {};
          const jcItems = Array.isArray(jcDetailResponse?.item_data)
            ? jcDetailResponse.item_data
            : [];

          const matchedJcItem = jcItems.find(
            (jcItem) =>
              Number(jcItem?.ref_item_serial_no || jcItem?.serial_no || 0) ===
                Number(row?.serial_no || index + 1) ||
              Number(jcItem?.product_id || 0) ===
                Number(row?.product_id || 0) ||
              String(jcItem?.product_name || "")
                .trim()
                .toLowerCase() ===
                String(row?.product_name || row?.Product_Name || "")
                  .trim()
                  .toLowerCase(),
          );

          if (!matchedJcItem && Object.keys(jcHead).length === 0) {
            continue;
          }

          const resolvedRouteCardId =
            matchedJcItem?.route_card_id ||
            matchedJcItem?.route_card ||
            jcHead?.route_card_id ||
            jcHead?.route_card_no ||
            row?.route_card_id ||
            row?.route_card ||
            null;
          const resolvedRouteCardNo =
            matchedJcItem?.route_card_no ||
            jcHead?.route_card_no ||
            row?.route_card_no ||
            (typeof matchedJcItem?.route_card === "string"
              ? matchedJcItem.route_card
              : "");

          nextRows[index] = {
            ...row,
            create_jobcard: row?.create_jobcard || "success",
            jc_tran_id: Number(resolvedJcTranId),
            job_card_id: Number(resolvedJcTranId),
            route_card_created: true,
            ...(resolvedRouteCardId
              ? { route_card_id: resolvedRouteCardId }
              : {}),
            ...(resolvedRouteCardId
              ? { route_card: resolvedRouteCardId }
              : { route_card: true }),
            ...(resolvedRouteCardNo
              ? { route_card_no: resolvedRouteCardNo }
              : {}),
          };
          hasChanges = true;
        } catch (error) {}
      }

      if (!isActive) return;

      hydratedWorkOrderRef.current.add(transactionKey);

      if (hasChanges) {
        setRows(nextRows);
        setTransactionItems(nextRows);
      }
    };

    hydrate();

    return () => {
      isActive = false;
    };
  }, [
    effectiveTransactionCode,
    rows,
    formData?.primaryDetails?.tran_id,
    transactionData?.headDetails?.tran_id,
    setTransactionItems,
  ]);

  const handleAddNewProduct = (e) => {
    e.stopPropagation();
    if (isStockAllocationTransaction) {
      navigate("/inventory/inventory-product-master/transactions", {
        state: {
          transactionCode: "IPM",
          isAddMode: true,
          transactionSettings: { type: "master" },
        },
      });
      return;
    }
    setShowProductModal(true);
    setIsPopupOpen(false);
  };

  const handleProductModalClose = () => {
    setShowProductModal(false);
    if (productJustCreatedRef.current) {
      productJustCreatedRef.current = false;
      return;
    }
    if (currentField?.db_query) {
      handlefetchTransactionPopupData(currentField.db_query);
    }
    setIsPopupOpen(true);
  };

  const handleApprove = () => {
    setShowApproveConfirmation(true);
  };

  const handleConfirmApprove = async () => {
    try {
      setIsApprovalLoading(true);
      const submissionData = {
        tran_id: formData.primaryDetails.tran_id,
        approve_status: "approved",
      };

      const response = await approveTransaction(
        submissionData,
        transactionCode,
      );

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        setPrimaryDetails({ approve_status: "approved", reject_reason: "" });
        displayToast("Transaction approved successfully", "success");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error approving transaction:", error);
      displayToast("Failed to approve transaction", "error");
    } finally {
      setIsApprovalLoading(false);
      setShowApproveConfirmation(false);
    }
  };

  const handleHold = () => {
    setRejectReason("");
    setActionType("hold");
    setShowRejectDialog(true);
  };

  const handleReject = () => {
    setRejectReason("");
    setActionType("rejected");
    setShowRejectDialog(true);
  };

  const handleConfirmRejectOrHold = async () => {
    try {
      setIsApprovalLoading(true);
      const submissionData = {
        tran_id: formData.primaryDetails.tran_id,
        approve_status: actionType || "rejected",
        reject_reason: rejectReason,
      };

      const response = await approveTransaction(
        submissionData,
        transactionCode,
      );

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        const nextStatus = actionType || "rejected";
        setPrimaryDetails({
          approve_status: nextStatus,
          reject_reason: rejectReason,
        });
        displayToast(
          nextStatus === "hold"
            ? "Transaction moved to hold successfully"
            : "Transaction rejected successfully",
          "success",
        );
        navigate(-1);
      }
    } catch (error) {
      console.error("Error rejecting transaction:", error);
      displayToast("Failed to reject transaction", "error");
    } finally {
      setIsApprovalLoading(false);
      setShowRejectDialog(false);
      setRejectReason("");
      setActionType("");
    }
  };

  const handleEnterValidation = async (field, rowIndex) => {
    if (!field.enter_validation) return;

    if (field.enter_validation.is_db_query && field.enter_validation.db_query) {
      try {
        const response = await fetchTransactionPopupData(
          field.enter_validation.db_query,
        );

        if (response.items && field.enter_validation.field_updation) {
          field.enter_validation.field_updation.forEach((update) => {
            const {
              copy_from,
              copy_from_field_name,
              copy_to_field_name,
              copy_to_table_type,
            } = update;

            if (copy_to_table_type === "item") {
              if (copy_from === "db") {
                const value = response.items[0]?.[copy_from_field_name];
                setItems((prevItems) => {
                  const newItems = [...prevItems];
                  if (!newItems[rowIndex]) {
                    newItems[rowIndex] = {};
                  }
                  newItems[rowIndex][copy_to_field_name] = value;
                  return newItems;
                });
              } else if (copy_from === "item") {
                const value = items[rowIndex]?.[copy_from_field_name];
                setItems((prevItems) => {
                  const newItems = [...prevItems];
                  if (!newItems[rowIndex]) {
                    newItems[rowIndex] = {};
                  }
                  newItems[rowIndex][copy_to_field_name] = value;
                  return newItems;
                });
              }
            }
          });
        }
      } catch (error) {
        console.error("Table Validation Error:", error);
      }
    }
  };

  const handleLeaveValidation = async (field, rowIndex) => {
    if (!field.leave_validation) return;

    if (field.leave_validation.is_db_query && field.leave_validation.db_query) {
      try {
        const response = await fetchTransactionPopupData(
          field.leave_validation.db_query,
        );

        if (response.items && field.leave_validation.field_updation) {
          field.leave_validation.field_updation.forEach((update) => {
            const {
              copy_from,
              copy_from_field_name,
              copy_to_field_name,
              copy_to_table_type,
            } = update;

            if (copy_to_table_type === "item") {
              if (copy_from === "db") {
                const value = response.items[0]?.[copy_from_field_name];
                setItems((prevItems) => {
                  const newItems = [...prevItems];
                  if (!newItems[rowIndex]) {
                    newItems[rowIndex] = {};
                  }
                  newItems[rowIndex][copy_to_field_name] = value;
                  return newItems;
                });
              } else if (copy_from === "item") {
                const value = items[rowIndex]?.[copy_from_field_name];
                setItems((prevItems) => {
                  const newItems = [...prevItems];
                  if (!newItems[rowIndex]) {
                    newItems[rowIndex] = {};
                  }
                  newItems[rowIndex][copy_to_field_name] = value;
                  return newItems;
                });
              }
            }
          });
        }
      } catch (error) {
        console.error("Table Leave Validation Error:", error);
      }
    }
  };

  const handleCancel = () => {
    clearOtherDetails();
    resetTransactionTable();
    clearForm();
    if (transactionCode === "BOM" || transactionCode === "PL") {
      navigate(-1);
    } else {
      navigate(`/${tab}/${category}`, {
        state: { transactionCode },
      });
    }
  };

  const handleRouteCardClick = async (row, rowIndex = 0) => {
    const workOrderNo =
      formData?.primaryDetails?.workorder_no ||
      formData?.primaryDetails?.wo_no ||
      formData?.primaryDetails?.tran_no;
    const tranId =
      formData?.primaryDetails?.tran_id ||
      transactionData?.headDetails?.tran_id ||
      row?.tran_id;

    let selectedRowForPopup = { ...row };

    if (transactionCode === "WO") {
      const createdJobCardRawValue =
        row?.create_jobcard ?? row?.CREATE_JOBCARD ?? row?.job_card_created;
      const createdJobCardFlag = [
        "success",
        "created",
        "1",
        "true",
        "yes",
        "y",
      ].includes(
        String(createdJobCardRawValue || "")
          .trim()
          .toLowerCase(),
      );

      let hasPerItemJobCardId = Boolean(
        row?.jc_tran_id || row?.job_card_id || row?.jobcard_id,
      );

      const hasExplicitRouteCardIdField = Boolean(
        row?.route_card_id || row?.routecard_id || row?.route_card_head_id,
      );
      const existingRouteCardId = hasExplicitRouteCardIdField
        ? getRouteCardIdFromRow(row)
        : null;
      if (!hasPerItemJobCardId && existingRouteCardId) {
        try {
          const existingRouteCardResponse =
            await fetchRouteCard(existingRouteCardId);
          const existingHeadData =
            existingRouteCardResponse?.head_data ||
            existingRouteCardResponse?.data?.head_data ||
            {};
          const existingJcTranId =
            existingHeadData?.jc_tran_id ||
            existingHeadData?.job_card_id ||
            null;

          if (existingJcTranId) {
            const updatedRows = rows.map((itemRow, index) =>
              index === rowIndex
                ? {
                    ...itemRow,
                    jc_tran_id: Number(existingJcTranId),
                    job_card_id: Number(existingJcTranId),
                  }
                : itemRow,
            );

            setRows(updatedRows);
            setTransactionItems(updatedRows);
            selectedRowForPopup = updatedRows[rowIndex] || selectedRowForPopup;
            hasPerItemJobCardId = true;
          }
        } catch (error) {}
      }

      if (!hasPerItemJobCardId) {
        const existingJobCardId = await resolveJobCardTranIdFromList(
          row,
          rowIndex,
        );

        if (existingJobCardId) {
          const updatedRows = rows.map((itemRow, index) =>
            index === rowIndex
              ? {
                  ...itemRow,
                  create_jobcard: "success",
                  jc_tran_id: Number(existingJobCardId),
                  job_card_id: Number(existingJobCardId),
                }
              : itemRow,
          );

          setRows(updatedRows);
          setTransactionItems(updatedRows);
          selectedRowForPopup = updatedRows[rowIndex] || selectedRowForPopup;
          hasPerItemJobCardId = true;
        }
      }

      if (!hasPerItemJobCardId) {
        if (createdJobCardFlag) {
          displayToast(
            "Job Card is already created for this item, but its ID could not be resolved. Please refresh once and try again.",
            "error",
          );
          return;
        }

        try {
          const response = await createJobcard({
            tran_id: row?.tran_id,
            serial_no: row?.serial_no || rowIndex + 1,
          });

          if (response?.status === "success") {
            let createdJobCardId = resolveJobCardTranId(response);

            if (!createdJobCardId) {
              createdJobCardId = await resolveJobCardTranIdFromList(
                row,
                rowIndex,
              );
            }

            const updatedRows = rows.map((itemRow, index) =>
              index === rowIndex
                ? {
                    ...itemRow,
                    create_jobcard: "success",
                    ...(createdJobCardId
                      ? { jc_tran_id: createdJobCardId }
                      : {}),
                    ...(createdJobCardId
                      ? { job_card_id: createdJobCardId }
                      : {}),
                  }
                : itemRow,
            );

            setRows(updatedRows);
            setTransactionItems(updatedRows);
            selectedRowForPopup = updatedRows[rowIndex] || selectedRowForPopup;
            hasPerItemJobCardId = Boolean(createdJobCardId);
          } else if (response?.description) {
            displayToast(response.description, "error");
            return;
          }
        } catch (error) {
          displayToast(
            "Unable to create Job Card for this item. Please try again.",
            "error",
          );
          return;
        }
      }

      if (!hasPerItemJobCardId) {
        displayToast(
          "Unable to resolve item Job Card ID. Please open this item from Job Card list once and try again.",
          "error",
        );
        return;
      }
    }

    if (!workOrderNo) {
      console.error("Route Card Creation - Missing Work Order Number");
      displayToast(
        "Work Order Number is required for route card creation",
        "error",
      );
      return;
    }

    if (!tranId) {
      console.error("Route Card Creation - Missing Transaction ID");
      displayToast(
        "Transaction ID is required for route card creation",
        "error",
      );
      return;
    }

    setSelectedRow({
      ...selectedRowForPopup,
      serial_no:
        selectedRowForPopup?.serial_no ||
        selectedRowForPopup?.serialNo ||
        rowIndex + 1,
      row_index: rowIndex,
      product_id: selectedRowForPopup?.product_id || null,
      jc_tran_id:
        selectedRowForPopup?.jc_tran_id ||
        selectedRowForPopup?.job_card_id ||
        selectedRowForPopup?.jobcard_id ||
        (transactionCode === "WO" ? null : tranId),
      wo_no: workOrderNo,
    });

    setShowRouteCardPopup(true);
  };

  return (
    <div className={`transaction-card ${modeHighlight}`}>
      <div className="transaction-card-content">
        <div className="table-container">
          <table className="transaction-table">
            <thead>
              {transactionCode === "EC" &&
                (() => {
                  const headingCells = [];
                  let i = 0;

                  while (i < visibleTableFields.length) {
                    const field = visibleTableFields[i];
                    const groupHeading =
                      EC_HEADING_GROUP_BY_FIELD[field.field_name] || "";

                    if (groupHeading) {
                      let colspan = 0;
                      let j = i;

                      while (j < visibleTableFields.length) {
                        const nextGroupHeading =
                          EC_HEADING_GROUP_BY_FIELD[
                            visibleTableFields[j].field_name
                          ] || "";
                        if (nextGroupHeading === groupHeading) {
                          colspan += 1;
                          j += 1;
                        } else {
                          break;
                        }
                      }

                      headingCells.push(
                        <th
                          key={`ec-group-${i}`}
                          colSpan={colspan}
                          className="ec-group-heading"
                        >
                          {groupHeading}
                        </th>,
                      );
                      i = j;
                    } else {
                      let colspan = 0;
                      let j = i;

                      while (j < visibleTableFields.length) {
                        const nextGroupHeading =
                          EC_HEADING_GROUP_BY_FIELD[
                            visibleTableFields[j].field_name
                          ] || "";
                        if (!nextGroupHeading) {
                          colspan += 1;
                          j += 1;
                        } else {
                          break;
                        }
                      }

                      headingCells.push(
                        <th
                          key={`ec-group-empty-${i}`}
                          colSpan={colspan}
                          className="ec-group-spacer"
                        ></th>,
                      );
                      i = j;
                    }
                  }

                  if (!isViewMode) {
                    headingCells.push(
                      <th
                        key="ec-group-remove"
                        className="ec-group-spacer"
                      ></th>,
                    );
                  }

                  return <tr className="ec-heading-row">{headingCells}</tr>;
                })()}
              <tr>
                {transactionCode !== "EC" && <th>Sl.No</th>}
                {visibleTableFields.map((field) => (
                  <th
                    key={field.field_name}
                    className={
                      transactionCode === "EC" &&
                      EC_YELLOW_HEADING_FIELDS.has(field.field_name)
                        ? "ec-highlight-heading"
                        : ""
                    }
                  >
                    {transactionCode === "WO" && isCreateJobCardField(field)
                      ? workOrderTypeLabel
                      : field.title || field.field_name}
                    {!isViewMode && field.is_required && (
                      <span className="required-mark">*</span>
                    )}
                  </th>
                ))}
                {!isViewMode && <th>Remove</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                return (
                  <tr
                    key={index}
                    style={
                      row?.status === "Possible"
                        ? { backgroundColor: "#d4edda" }
                        : row?.status === "Not possible"
                          ? { backgroundColor: "#f8d7da" }
                          : {}
                    }
                  >
                    {transactionCode !== "EC" && <td>{index + 1}</td>}
                    {visibleTableFields.map((field) => (
                      <td
                        key={field.field_name}
                        className={
                          String(
                            `${field.field_type || ""} ${field.data_type || ""}`,
                          )
                            .toLowerCase()
                            .includes("file_upload") ||
                          field.field_name === "inspection_report"
                            ? "file-upload-cell"
                            : ""
                        }
                      >
                        {renderFieldContent(row, field, index)}
                      </td>
                    ))}
                    {!isViewMode && (
                      <td>
                        <button
                          className="remove-row-btn"
                          onClick={() => removeRow(index)}
                          title="Remove Row"
                        >
                          ✕
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={`transaction-detail-container${transactionCode === "WO" ? " workorder-table-actions" : ""}`}>
          <div className="add-item-container">
            {!isViewMode && jobCardStatus !== "finished" && (
              <button
                className="add-item-btn"
                onClick={addRow}
                disabled={pickupMandatory}
              >
                Add Item
                <svg
                  className="plus-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            )}
            {type !== "JobCard" && (
              <div className="total-quantity">
                <span>Total Quantity: {calculateTotalQuantity()}</span>
              </div>
            )}
          </div>

          {tab === "approval" && !isEditMode && (
            <div className="approval-buttons-container">
              {formData.primaryDetails?.approve_status === "approved" ? (
                <div className="status-approved">APPROVED</div>
              ) : formData.primaryDetails?.approve_status === "rejected" ? (
                <div className="rejected-dropdown-container">
                  <div className="status-rejected">REJECTED</div>
                  <div className="view-reason-wrapper">
                    {showRejectReason && (
                      <div className="popup-tooltip">
                        <div className="popup-arrow" />
                        <div className="popup-detail">
                          {formData.primaryDetails.reject_reason ||
                            "Not specified"}
                        </div>
                      </div>
                    )}

                    <button
                      className="reason-toggle-btn"
                      onClick={() => setShowRejectReason(!showRejectReason)}
                    >
                      {showRejectReason ? "Hide Reason" : "View Reason"}
                    </button>
                  </div>
                </div>
              ) : formData.primaryDetails?.approve_status === "hold" ? (
                <div className="rejected-dropdown-container">
                  <div className="status-hold">ON HOLD</div>
                  <div className="view-reason-wrapper">
                    {showRejectReason && (
                      <div className="popup-tooltip">
                        <div className="popup-arrow" />
                        <div className="popup-detail">
                          {formData.primaryDetails.reject_reason ||
                            "Not specified"}
                        </div>
                      </div>
                    )}

                    <button
                      className="reason-toggle-btn"
                      onClick={() => setShowRejectReason(!showRejectReason)}
                    >
                      {showRejectReason ? "Hide Reason" : "View Reason"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    className="approval-button approval-button--approve"
                    onClick={handleApprove}
                    disabled={isApprovalLoading}
                    type="button"
                  >
                    {isApprovalLoading ? "Processing..." : "APPROVE"}
                  </button>
                  {!isEditMode && (
                    <button
                      className="approval-button approval-button--hold"
                      onClick={handleHold}
                      disabled={isApprovalLoading}
                      type="button"
                    >
                      {isApprovalLoading ? "Processing..." : "HOLD"}
                    </button>
                  )}
                  <button
                    className="approval-button approval-button--reject"
                    onClick={handleReject}
                    disabled={isApprovalLoading}
                    type="button"
                  >
                    {isApprovalLoading ? "Processing..." : "REJECT"}
                  </button>
                </>
              )}
            </div>
          )}

          {!isViewMode && (
            <>
              <div className="table-save-button-container">
                {isButtonLoading ? (
                  <div className="loader-button-container">
                    <span className="loader-button"></span>
                  </div>
                ) : (
                  <>
                    {tab === "approval" && isEditMode ? (
                      <div className="edit-approval-save-grid">
                        <div className="edit-approval-save-col">
                          {(formData.primaryDetails?.approve_status ===
                            "hold" ||
                            formData.primaryDetails?.approve_status ===
                              "rejected") && (
                            <button
                              className="approval-button approval-button--approve"
                              onClick={handleApprove}
                              disabled={isApprovalLoading}
                              type="button"
                            >
                              {isApprovalLoading ? "Processing..." : "APPROVE"}
                            </button>
                          )}
                          <button className="cancel-btn" onClick={handleCancel}>
                            <XCircle size={16} />
                            CANCEL
                          </button>
                        </div>
                        <div className="edit-approval-save-col">
                          {formData.primaryDetails?.approve_status ===
                            "hold" && (
                            <button
                              className="approval-button approval-button--reject"
                              onClick={handleReject}
                              disabled={isApprovalLoading}
                              type="button"
                            >
                              {isApprovalLoading ? "Processing..." : "REJECT"}
                            </button>
                          )}

                          <button className="next-btn" onClick={handleSubmit}>
                            <Save size={16} />
                            SAVE
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button className="cancel-btn" onClick={handleCancel}>
                          <XCircle size={16} />
                          CANCEL
                        </button>
                        {draftSubmission && (
                          <button
                            type="button"
                            className="draft-save-btn"
                            onClick={draftSubmission.saveDraft}
                            disabled={
                              !draftSubmission.canAdd ||
                              draftSubmission.saving ||
                              draftSubmission.submitting ||
                              draftSubmission.minimizing ||
                              draftSubmission.committed
                            }
                            title="Keep this entry as a private draft without posting it"
                          >
                            <FileText size={16} />
                            {draftSubmission.saving && !draftSubmission.minimizing
                              ? "SAVING…"
                              : "SAVE DRAFT"}
                          </button>
                        )}
                        <button className="next-btn" onClick={handleSubmit}>
                          <Save size={16} />
                          SAVE
                        </button>
                        {isEditMode &&
                          transactionCode === "SQ" &&
                          Number(formData.primaryDetails?.is_latest ?? 1) !==
                            0 && (
                            <button
                              className="revise-quotation-btn"
                              onClick={handleReviseQuotation}
                              title="Save these edits as a new revision (R1, R2, …); the original stays untouched"
                            >
                              <Copy size={16} />
                              REVISED QUOTATION
                            </button>
                          )}
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {transactionCode !== "WO" && (
          <div
            className={`totals-wrapper ${
              type === "JobCard" && !isViewMode ? "totals-wrapper-jobcard" : ""
            }`}
          >
            {type !== "JobCard" && (
              <div className="totals-container">
                <div className="total-row">
                  <span>Subtotal:</span>
                  <span>Rs {subtotal.toFixed(2)}</span>
                </div>
                <div className="total-row total-bill-separator-line">
                  <span>Total Bill:</span>
                  <span>Rs {grossTotal.toFixed(2)}</span>
                </div>
                <div className="grand-total">
                  <span>Grand Total:</span>
                  <span>Rs {Number(grossTotal).toFixed(2)}</span>{" "}
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {isPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-content" ref={popUpModalRef}>
            <div className="popup-header">
              <h3>
                {type === "JobCard"
                  ? `Select ${capitalizeFirstLetterOfEachWord(
                      currentField.field_name,
                    )}`
                  : "Select Product"}
              </h3>
              <button
                className="close-button"
                onClick={handleCloseProductPopup}
              >
                &times;
              </button>
            </div>

            {type !== "JobCard" && (
              <>
                <div className="search-product">
                  <input
                    type="text"
                    className="search-product-input"
                    placeholder="Search product"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    ref={searchProductInputRef}
                    autoFocus
                  />
                </div>
                <div className="add-new-product">
                  <button
                    className="add-new-product-btn"
                    onClick={handleAddNewProduct}
                  >
                    + Add New Product
                  </button>
                </div>
              </>
            )}
            <ul>
              {popupItems
                .filter((item) =>
                  (
                    item.Product_Name ||
                    item.product_name ||
                    item.company_name ||
                    item.name ||
                    item.inventory_product_id
                  )
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase()),
                )
                .map((item) => (
                  <li
                    key={item.tran_id || item.id}
                    className="popup-product-item"
                    onClick={() => handlePopupItemClick(item)}
                    onMouseEnter={
                      isStockAllocationTransaction
                        ? () => handlePopupItemMouseEnter(item)
                        : undefined
                    }
                    onMouseLeave={
                      isStockAllocationTransaction
                        ? handlePopupItemMouseLeave
                        : undefined
                    }
                  >
                    <span className="popup-product-name">
                      {item.Product_Name ||
                        item.product_name ||
                        item.company_name ||
                        item.name ||
                        item.inventory_product_id}
                    </span>

                    {(() => {
                      if (!isStockAllocationTransaction) return null;

                      const productId = getPopupProductId(item);
                      if (!productId) return null;

                      const productKey = String(productId);
                      const showHoverCard =
                        popupHoveredProductId === productKey;
                      if (!showHoverCard) return null;

                      const isLoading =
                        !!popupProductAvailabilityLoading[productKey];
                      const stock = popupProductAvailability[productKey];

                      return (
                        <div className="popup-product-hover-card">
                          {isLoading ? (
                            <p className="popup-product-hover-loading">
                              Loading stock details...
                            </p>
                          ) : (
                            <>
                              <p className="popup-product-hover-row">
                                <strong>Current Stock:</strong>{" "}
                                {stock?.currentQty ?? "-"}
                              </p>
                              <p className="popup-product-hover-row">
                                <strong>Available:</strong>{" "}
                                {stock?.availableQty ?? "-"}
                              </p>
                              <p className="popup-product-hover-row">
                                <strong>Allocated:</strong>{" "}
                                {stock?.allocatedQty ?? "-"}
                              </p>
                              {stock?.error && (
                                <p className="popup-product-hover-error">
                                  {stock.error}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}

      {isMaterialPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-content" ref={materialPopupRef}>
            <div className="popup-header">
              <h3>Select Material</h3>
              <button
                className="close-button"
                onClick={() => setIsMaterialPopupOpen(false)}
              >
                &times;
              </button>
            </div>
            <ul>
              {EC_MATERIAL_OPTIONS.map((material) => (
                <li
                  key={material.name}
                  onClick={() => handleMaterialSelect(material)}
                >
                  {material.name}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {isOptionsPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-content" ref={optionsPopupRef}>
            <div className="popup-header">
              <h3>
                Select{" "}
                {optionsPopupField?.title || optionsPopupField?.field_name}
              </h3>
              <button
                className="close-button"
                onClick={() => setIsOptionsPopupOpen(false)}
              >
                &times;
              </button>
            </div>
            <ul>
              {optionsPopupItems.map((item) => (
                <li key={item} onClick={() => handleOptionsSelect(item)}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {showProductModal && (
        <Modal
          isOpen={showProductModal}
          onRequestClose={handleProductModalClose}
          className="transaction-other-detail-custom-modal"
          overlayClassName="transaction-other-detail-custom-overlay"
        >
          <ProductMaster
            transactionCode="PM"
            heading="Add New Product"
            modeOfEdit="add"
            onClose={handleProductModalClose}
            isPopupMode={true}
            showAllFields={["CO", "SO"].includes(effectiveTransactionCode)}
            prefillValues={(() => {
              if (!["CO", "SO"].includes(effectiveTransactionCode)) {
                return null;
              }
              const sourceRow =
                currentRowIndex !== null && currentRowIndex !== undefined
                  ? rows[currentRowIndex]
                  : null;
              if (!sourceRow) return null;
              return {
                name: sourceRow.product_name || "",
                product_name: sourceRow.product_name || "",
                selling_rate:
                  sourceRow.amount ??
                  sourceRow.gross_amount ??
                  sourceRow.gross_amt ??
                  "",
                uom: sourceRow.uom || "",
              };
            })()}
            onProductCreated={handleProductCreated}
          />
        </Modal>
      )}

      {showRejectDialog && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowRejectDialog(false);
              setRejectReason("");
              setIsApprovalLoading(false);
              setActionType("");
            }
          }}
        >
          <div className="modal-content">
            <p className="modal-message">
              {actionType === "hold"
                ? "Enter reason for hold"
                : "Enter reason for rejection"}
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="modal-textarea"
              placeholder="Enter reason"
              required
            />
            <div className="modal-buttons">
              <button
                className="modal-button confirm-button reject-button"
                onClick={handleConfirmRejectOrHold}
                disabled={!rejectReason.trim() || isApprovalLoading}
              >
                {isApprovalLoading
                  ? "Processing..."
                  : actionType === "hold"
                    ? "Hold"
                    : "Reject"}
              </button>
              <button
                className="modal-button cancel-button"
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectReason("");
                  setIsApprovalLoading(false);
                  setActionType("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showApproveConfirmation}
        onClose={() => {
          setShowApproveConfirmation(false);
          setIsApprovalLoading(false);
        }}
        onConfirm={handleConfirmApprove}
        message="Are you sure you want to approve this transaction?"
        confirmText={isApprovalLoading ? "Processing..." : "Approve"}
        cancelText="Cancel"
      />
      <ConfirmationModal
        isOpen={showJobCardRemoveConfirm}
        onClose={() => {
          setShowJobCardRemoveConfirm(false);
          setJobCardRowIndexToRemove(null);
        }}
        onConfirm={() => {
          if (jobCardRowIndexToRemove !== null) {
            handleRowRemoval(jobCardRowIndexToRemove);
            setJobCardRowIndexToRemove(null);
          }
          setShowJobCardRemoveConfirm(false);
        }}
        onCancel={() => {
          setShowJobCardRemoveConfirm(false);
          setJobCardRowIndexToRemove(null);
        }}
        message="Are you sure you want to remove this row?"
        confirmText="Remove"
        cancelText="Cancel"
      />

      {showRouteCardPopup && selectedRow && (
        <RouteCardPopup
          requireWorkOrderStart={transactionCode === "WO"}
          workOrderStatus={workOrderStatus}
          jc_tran_id={
            selectedRow?.jc_tran_id ||
            selectedRow?.job_card_id ||
            selectedRow?.jobcard_id ||
            (transactionCode === "WO"
              ? null
              : formData?.primaryDetails?.tran_id)
          }
          jc_tran_serial_no={selectedRow.serial_no}
          wo_no={
            formData?.primaryDetails?.workorder_no ||
            formData?.primaryDetails?.wo_no ||
            formData?.primaryDetails?.tran_no ||
            transactionData?.headDetails?.workorder_no ||
            transactionData?.headDetails?.wo_no ||
            transactionData?.headDetails?.tran_no
          }
          productName={selectedRow.product_name}
          product_id={selectedRow.product_id}
          productDescription={selectedRow.product_name}
          popupCardLabel={workOrderTypeLabel}
          routeCardNo={selectedRow.route_card_no || ""}
          routeProcessFields={rowData.filter((field) =>
            [
              "allocated_work_station",
              "time_taken",
              "start_time",
              "start",
              "finish",
            ].includes(String(field.field_name || "").toLowerCase()),
          )}
          onClose={() => {
            setShowRouteCardPopup(false);
            Promise.resolve(onRefresh()).catch(() => {});
          }}
          route_card_created={isRouteCardCreatedForRow(selectedRow)}
          route_card_id={getRouteCardIdFromRow(selectedRow)}
          onRouteCardSaved={(savedRouteCardId, response) => {
            const headDataResponse =
              response?.head_data || response?.data?.head_data || {};
            const savedRouteCardNo = headDataResponse.route_card_no || "";
            const savedJcTranId =
              headDataResponse?.jc_tran_id ||
              headDataResponse?.job_card_id ||
              null;
            const responseItems = Array.isArray(response?.item_data)
              ? response.item_data
              : Array.isArray(response?.data?.item_data)
                ? response.data.item_data
                : [];
            const hasPersistedRouteCard = Boolean(
              savedRouteCardId ||
              savedRouteCardNo ||
              savedJcTranId ||
              responseItems.length > 0,
            );
            const finishToken = String(
              headDataResponse?.route_card_finish || "",
            )
              .trim()
              .toLowerCase();
            const isHeadFinished = [
              "finished",
              "finish",
              "completed",
              "complete",
              "done",
              "closed",
              "2",
            ].includes(finishToken);
            const routeCardUpdates = {
              ...(hasPersistedRouteCard
                ? {
                    route_card_created: true,
                  }
                : {}),
              ...(savedRouteCardId
                ? {
                    route_card: savedRouteCardId,
                    route_card_id: savedRouteCardId,
                  }
                : {}),
              ...(savedJcTranId ? { jc_tran_id: Number(savedJcTranId) } : {}),
              ...(savedJcTranId ? { job_card_id: Number(savedJcTranId) } : {}),
              ...(savedRouteCardNo && { route_card_no: savedRouteCardNo }),
              ...(isHeadFinished
                ? {
                    route_card_finish: "finished",
                    finish: "finished",
                    route_card_head_finished: true,
                  }
                : {}),
            };

            setSelectedRow((prev) => ({
              ...prev,
              ...routeCardUpdates,
            }));
            const selectedRowIndex = Number(selectedRow?.row_index);
            const hasValidRowIndex =
              Number.isInteger(selectedRowIndex) && selectedRowIndex >= 0;

            const updatedRows = rows.map((row, index) => {
              const isMatchedByIndex =
                hasValidRowIndex && index === selectedRowIndex;
              const isMatchedBySerial =
                !hasValidRowIndex && row.serial_no === selectedRow.serial_no;

              return isMatchedByIndex || isMatchedBySerial
                ? {
                    ...row,
                    ...routeCardUpdates,
                  }
                : row;
            });
            setRows(updatedRows);
            setTransactionItems(updatedRows);
          }}
        />
      )}
    </div>
  );
};

export default TransactionTable;
